import Matter from "matter-js";
import { ArcadeRuntime, isArcadeGameId, type ArcadeEvent, type ArcadeGameId } from "./arcade";
import { DEFAULT_GAME_ID, GAME_CATALOG, gameById, isPlayableGame, normalizeGameId, type GameId } from "../shared/games";
import {
  catalogForPack,
  holeById,
  holeIdsForRound,
  holeFor,
  holeFamilyId,
  holeForRound,
  holesForRound,
  holesForRoundIds,
  movingRailCenter,
  rotatingBunkerCircles,
  type DiscZone,
  type HoleDefinition,
  type MovingRailObstacle,
  type SegmentObstacle,
  type TriangleObstacle,
} from "../shared/courses";
import type { ClientMessage, PlayerSlot, PlayerStatus, RoomSnapshot, ServerMessage, Point, PlayMode, CoursePack, PowerUpKind, PowerUpSettings } from "../shared/messages";
import { MAX_HOLES, MAX_PLAYERS, PLAYER_SLOTS, clampHoleCount, isPowerUpEnabled, normalizeCoursePack, normalizePlayMode, normalizePowerUps } from "../shared/messages";
import "./styles.css";

const {
  Body,
  Bodies,
  Composite,
  Engine,
  Events,
  Vector,
  World,
} = Matter;

const BALL_RADIUS = 12;
const WALL_BOUNCE_RESTITUTION = 0.86;
const WALL_FRICTION_DAMPING = 0.992;
const REST_SPEED = 0.14;
const MAX_SHOT_SPEED = 26;
const FULL_PULL_POWER = 280;
const SHOT_POWER_EXPONENT = 1.32;
const AIM_CAPTURE_SCREEN_RADIUS = 58;
const TEE_SLOT_OFFSET = 11;
const GHOST_BODY_CORRECTION = 0.07;
const GHOST_BODY_SNAP_DISTANCE = 170;
const GHOST_VISUAL_SNAP_DISTANCE = 520;
const GHOST_TRAIL_INTERVAL = 42;
const SLOT_COLORS: Record<PlayerSlot, string> = {
  A: "#ffffff",
  B: "#6de5ff",
  C: "#ffd45c",
  D: "#ff7ac8",
};
const SLOT_OFFSETS: Record<PlayerSlot, Point> = {
  A: { x: -TEE_SLOT_OFFSET, y: -TEE_SLOT_OFFSET * 0.7 },
  B: { x: TEE_SLOT_OFFSET, y: -TEE_SLOT_OFFSET * 0.7 },
  C: { x: -TEE_SLOT_OFFSET, y: TEE_SLOT_OFFSET * 0.85 },
  D: { x: TEE_SLOT_OFFSET, y: TEE_SLOT_OFFSET * 0.85 },
};
const BALL_CATEGORY = 0x0002;
const GHOST_CATEGORY = 0x0004;
const WALL_CATEGORY = 0x0008;
const RECENT_ROUND_HOLES_KEY = "pigeon-putt:recent-round-holes";
const RECENT_ROUND_OPENERS_KEY = "pigeon-putt:recent-round-openers";
const RECENT_ROUND_HOLE_LIMIT = 64;
const RECENT_ROUND_OPENER_LIMIT = 18;
const POWER_UPS_KEY = "pigeon-putt:power-ups";

type Ghost = {
  slot: PlayerSlot;
  body: Matter.Body;
  trail: Point[];
  moving: boolean;
  lastSeen: number;
  lastTrailAt: number;
  renderPosition: Point;
  targetPosition: Point;
  targetVelocity: Point;
};

type Camera = {
  x: number;
  y: number;
  zoom: number;
};

type PointerMode =
  | { type: "idle" }
  | { type: "aim"; id: number; start: Point; current: Point; screenStart: Point; screenCurrent: Point }
  | { type: "pan"; id: number; lastScreen: Point };

type GameMode = "idle" | "matchmaking" | "multi" | "single";

type AdvanceOverlay = {
  previousHole: number;
  nextHole: number;
  title: string;
  copy: string;
  until: number;
};

type RoundSelection = {
  seed: string;
  holeIds: number[];
};

const state = {
  apiOrigin: getApiOrigin(),
  clientId: getClientId(),
  playerName: getInitialName(),
  socket: undefined as WebSocket | undefined,
  matchSocket: undefined as WebSocket | undefined,
  snapshot: undefined as RoomSnapshot | undefined,
  localSlot: undefined as PlayerSlot | undefined,
  mode: "idle" as GameMode,
  matchIntent: undefined as "host" | "join" | undefined,
  matchTicket: undefined as string | undefined,
  homePanel: "menu" as "menu" | "host" | "solo" | "powerups",
  powerUpsReturn: "host" as "host" | "solo",
  waitingHosts: 0,
  gameId: DEFAULT_GAME_ID as GameId,
  playMode: "strokes" as PlayMode,
  coursePack: "classic" as CoursePack,
  powerUps: getInitialPowerUps() as PowerUpSettings,
  controlMode: "shoot" as "shoot" | "look",
  autoReady: false,
  localHole: -1,
  localStrokes: 0,
  holeComplete: false,
  engine: Engine.create({ gravity: { x: 0, y: 0 } }),
  ball: undefined as Matter.Body | undefined,
  ghosts: new Map<string, Ghost>(),
  currentHole: holeFor(0),
  dynamicRails: [] as Array<{ obstacle: MovingRailObstacle; body: Matter.Body }>,
  zoneCooldowns: new Map<number, Map<string, number>>(),
  airborneBodies: new Map<number, { until: number; duration: number }>(),
  camera: { x: 450, y: 340, zoom: 0.8 } as Camera,
  pointer: { type: "idle" } as PointerMode,
  lastFrameAt: performance.now(),
  lastStateSentAt: 0,
  lastStoppedSentAt: 0,
  showDottedAim: true,
  didReportComplete: false,
  advanceOverlay: undefined as AdvanceOverlay | undefined,
  arcadeRoundKey: "",
};

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing app root");

let pendingCanvasRefit: number | undefined;
const gameCardsHtml = GAME_CATALOG.map(
  (game) => `
    <button class="game-card ${game.status === "playable" ? "playable" : "planned"}" id="game-${game.id}" type="button" data-game-id="${game.id}" aria-label="${game.title}">
      <span class="game-card-top">
        <strong>${game.title}</strong>
        <em>${game.players}</em>
      </span>
      <span>${game.summary}</span>
      <small>${game.tag}</small>
    </button>
  `,
).join("");

app.innerHTML = `
  <main class="shell">
    <header class="topbar">
      <div class="brand">
        <div class="mascot" aria-hidden="true">P</div>
        <div>
          <h1>Pigeon Putt</h1>
          <p>Live mini golf for two</p>
        </div>
      </div>
      <div class="connection" id="connection">Offline</div>
    </header>

    <section class="layout">
      <aside class="panel lobby-panel" id="lobbyPanel">
        <div class="home-copy">
          <strong>Play a round</strong>
          <span id="homeStatus">Host a game, join an open table, or play solo.</span>
        </div>
        <div class="game-picker" id="gamePicker" role="group" aria-label="Game">
          ${gameCardsHtml}
        </div>
        <div class="panel-section name-section">
          <label for="playerName">Name</label>
          <input id="playerName" maxlength="24" autocomplete="nickname" />
        </div>
        <div class="panel-section host-config">
          <label for="holeCount">Holes</label>
          <div class="stepper">
            <button id="holeMinus" type="button" aria-label="Fewer holes">-</button>
            <input id="holeCount" type="number" min="1" max="${MAX_HOLES}" value="5" />
            <button id="holePlus" type="button" aria-label="More holes">+</button>
          </div>
        </div>
        <div class="panel-section host-config golf-config">
          <label>Game</label>
          <div class="segmented" role="group" aria-label="Game mode">
            <button id="modeStrokes" type="button">Stroke</button>
            <button id="modeRace" type="button">Race</button>
          </div>
        </div>
        <div class="panel-section host-config golf-config">
          <label>Courses</label>
          <div class="segmented course-segmented" role="group" aria-label="Course pack">
            <button id="packClassic" type="button">Classic</button>
            <button id="packSpecial" type="button">Special</button>
            <button id="packLong" type="button">Long</button>
          </div>
        </div>
        <div class="panel-section host-config golf-config">
          <label>Power-ups</label>
          <button class="settings-link" id="powerUpsOpen" type="button" aria-label="Power-up Settings">
            <span>Power-up Settings</span>
            <strong id="powerUpsSummary">All on</strong>
            <span aria-hidden="true">&gt;</span>
          </button>
        </div>
        <div class="powerups-page" id="powerupsPage">
          <div class="panel-section">
            <label>Power-ups</label>
            <button class="toggle-row powerup-toggle" id="powerUpJump" type="button" aria-pressed="true">
              <span class="toggle-track" aria-hidden="true"><span></span></span>
              <strong>Jump Pads</strong>
            </button>
            <button class="toggle-row powerup-toggle" id="powerUpBumper" type="button" aria-pressed="true">
              <span class="toggle-track" aria-hidden="true"><span></span></span>
              <strong>Bumpers</strong>
            </button>
            <button class="toggle-row powerup-toggle" id="powerUpPortal" type="button" aria-pressed="true">
              <span class="toggle-track" aria-hidden="true"><span></span></span>
              <strong>Portals</strong>
            </button>
          </div>
        </div>
        <div class="mode-actions">
          <button class="mode-action host-action primary" id="createRoom" type="button" aria-label="Host Game">
            <span class="action-badge host-badge" aria-hidden="true"></span>
            <span class="action-body">
              <strong class="action-title">Host Game</strong>
              <span class="action-copy">Open table</span>
            </span>
            <span class="action-cue" aria-hidden="true">&gt;</span>
          </button>
          <button class="mode-action join-action" id="joinRoom" type="button" aria-label="Join Game">
            <span class="action-badge join-badge" aria-hidden="true"></span>
            <span class="action-body">
              <strong class="action-title">Join Game</strong>
              <span class="action-copy">Enter match</span>
            </span>
            <span class="action-cue" aria-hidden="true">&gt;</span>
          </button>
          <button class="mode-action solo-action" id="singlePlayer" type="button" aria-label="Single Player">
            <span class="action-badge solo-badge" aria-hidden="true"></span>
            <span class="action-body">
              <strong class="action-title">Single Player</strong>
              <span class="action-copy">Practice run</span>
            </span>
            <span class="action-cue" aria-hidden="true">&gt;</span>
          </button>
        </div>
        <div class="queue-card" id="queueCard" aria-live="polite">
          <div class="queue-mark" aria-hidden="true">
            <span></span>
          </div>
          <div class="queue-copy">
            <strong id="queueTitle">Waiting for player</strong>
            <span id="queueCopy">Keep this screen open. The next player who joins enters this match.</span>
          </div>
          <div class="queue-summary" id="queueSummary">Classic · 5 holes · All on</div>
          <button class="queue-cancel" id="cancelQueue" type="button">Cancel Hosting</button>
        </div>
        <button class="lobby-start-action" id="lobbyStartButton" type="button">Start Match</button>
        <button class="back-action" id="backHome" type="button">Back</button>
        <p class="small" id="shareHint">Host uses your settings. Join enters the open hosted game.</p>
      </aside>

      <section class="game-wrap">
        <canvas id="gameCanvas" aria-label="Mini golf course"></canvas>
        <div class="hud top-hud">
          <div>
            <span class="label">Mode</span>
            <strong id="roomCode">----</strong>
          </div>
          <div>
            <span class="label" id="holeMetaLabel">Hole</span>
            <strong id="holeLabel">1/5</strong>
          </div>
          <div>
            <span class="label" id="parMetaLabel">Par</span>
            <strong id="parLabel">3</strong>
          </div>
        </div>
        <div class="play-scoreboard" id="playScoreboard" aria-label="Live stroke counts"></div>
        <div class="hud bottom-hud">
          <button class="tool-button zoom-out" id="zoomOut" type="button" title="Zoom out" aria-label="Zoom out"><span aria-hidden="true"></span></button>
          <button class="tool-button fit-tool" id="fitCourse" type="button" title="Fit course" aria-label="Fit course"><span aria-hidden="true"></span></button>
          <button class="tool-button zoom-in" id="zoomIn" type="button" title="Zoom in" aria-label="Zoom in"><span aria-hidden="true"></span></button>
          <button id="readyButton" class="accent tool-ready" type="button">Ready</button>
        </div>
        <div class="overlay" id="gameOverlay">
          <div class="overlay-card">
            <div class="overlay-title" id="overlayTitle">Choose a mode</div>
            <div class="overlay-copy" id="overlayCopy">Host a game, join an open game, or start a local round.</div>
          </div>
        </div>
      </section>

      <aside class="panel score-panel">
        <div class="score-head">
          <div>
            <span class="label">Players</span>
            <strong id="playerCount">0/4</strong>
          </div>
          <button id="restartGame" type="button">Restart</button>
        </div>
        <div class="results-banner" id="resultsBanner">
          <strong id="resultsTitle">Match complete</strong>
          <span id="resultsCopy">Final scores are below.</span>
        </div>
        <div class="players" id="players"></div>
        <div class="scorecard" id="scorecard"></div>
      </aside>
    </section>
  </main>
`;

const canvas = must(document.querySelector<HTMLCanvasElement>("#gameCanvas"), "game canvas");
const playerNameInput = must(document.querySelector<HTMLInputElement>("#playerName"), "player name input");
const holeCountInput = must(document.querySelector<HTMLInputElement>("#holeCount"), "hole count input");
const createRoomButton = must(document.querySelector<HTMLButtonElement>("#createRoom"), "create room button");
const joinRoomButton = must(document.querySelector<HTMLButtonElement>("#joinRoom"), "join room button");
const singlePlayerButton = must(document.querySelector<HTMLButtonElement>("#singlePlayer"), "single player button");
const gameButtons = Array.from(document.querySelectorAll<HTMLButtonElement>(".game-card"));
const cancelQueueButton = must(document.querySelector<HTMLButtonElement>("#cancelQueue"), "cancel queue button");
const lobbyStartButton = must(document.querySelector<HTMLButtonElement>("#lobbyStartButton"), "lobby start button");
const backHomeButton = must(document.querySelector<HTMLButtonElement>("#backHome"), "back home button");
const readyButton = must(document.querySelector<HTMLButtonElement>("#readyButton"), "ready button");
const restartButton = must(document.querySelector<HTMLButtonElement>("#restartGame"), "restart button");
const modeStrokesButton = must(document.querySelector<HTMLButtonElement>("#modeStrokes"), "stroke mode button");
const modeRaceButton = must(document.querySelector<HTMLButtonElement>("#modeRace"), "race mode button");
const packClassicButton = must(document.querySelector<HTMLButtonElement>("#packClassic"), "classic pack button");
const packSpecialButton = must(document.querySelector<HTMLButtonElement>("#packSpecial"), "special pack button");
const packLongButton = must(document.querySelector<HTMLButtonElement>("#packLong"), "long pack button");
const powerUpsOpenButton = must(document.querySelector<HTMLButtonElement>("#powerUpsOpen"), "power ups settings button");
const powerUpsSummary = must(document.querySelector<HTMLElement>("#powerUpsSummary"), "power ups summary");
const powerUpButtons: Record<PowerUpKind, HTMLButtonElement> = {
  jump: must(document.querySelector<HTMLButtonElement>("#powerUpJump"), "jump power up button"),
  bumper: must(document.querySelector<HTMLButtonElement>("#powerUpBumper"), "bumper power up button"),
  portal: must(document.querySelector<HTMLButtonElement>("#powerUpPortal"), "portal power up button"),
};
const overlay = must(document.querySelector<HTMLDivElement>("#gameOverlay"), "game overlay");
const overlayTitle = must(document.querySelector<HTMLDivElement>("#overlayTitle"), "overlay title");
const overlayCopy = must(document.querySelector<HTMLDivElement>("#overlayCopy"), "overlay copy");
const playScoreboard = must(document.querySelector<HTMLDivElement>("#playScoreboard"), "live scoreboard");
const resultsTitle = must(document.querySelector<HTMLElement>("#resultsTitle"), "results title");
const resultsCopy = must(document.querySelector<HTMLElement>("#resultsCopy"), "results copy");
const homeStatus = must(document.querySelector<HTMLSpanElement>("#homeStatus"), "home status");
const shareHint = must(document.querySelector<HTMLParagraphElement>("#shareHint"), "share hint");
const queueTitle = must(document.querySelector<HTMLElement>("#queueTitle"), "queue title");
const queueCopy = must(document.querySelector<HTMLElement>("#queueCopy"), "queue copy");
const queueSummary = must(document.querySelector<HTMLElement>("#queueSummary"), "queue summary");
const ctx = must(canvas.getContext("2d"), "canvas context");
const arcadeRuntime = new ArcadeRuntime(() => ({
  canvas,
  ctx,
  localPlayerId: state.clientId,
  localSlot: state.localSlot,
  players: state.snapshot?.players ?? [],
  phase: state.snapshot?.phase,
  sendEvent: sendArcadeEvent,
  updateScore: updateArcadeScore,
  playTap,
}));
const turfImage = loadArtImage("/assets/turf-aaa.jpg");
let turfPattern: CanvasPattern | undefined;

playerNameInput.value = state.playerName;

const roomFromUrl = new URLSearchParams(location.search).get("room");
if (roomFromUrl) {
  connectRoom(roomFromUrl.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6), { replaceHistory: false, autoReady: false });
}

playerNameInput.addEventListener("input", () => {
  state.playerName = playerNameInput.value.trim() || "Player";
  localStorage.setItem("pigeon-putt:name", state.playerName);
  send({ type: "set_name", name: state.playerName });
  updateUi();
});

document.querySelector("#holeMinus")?.addEventListener("click", () => setHoleInput(Number(holeCountInput.value) - 1));
document.querySelector("#holePlus")?.addEventListener("click", () => setHoleInput(Number(holeCountInput.value) + 1));
holeCountInput.addEventListener("change", () => setHoleInput(Number(holeCountInput.value)));
modeStrokesButton.addEventListener("click", () => setPlayMode("strokes"));
modeRaceButton.addEventListener("click", () => setPlayMode("race"));
packClassicButton.addEventListener("click", () => setCoursePack("classic"));
packSpecialButton.addEventListener("click", () => setCoursePack("special"));
packLongButton.addEventListener("click", () => setCoursePack("long"));
for (const button of gameButtons) {
  button.addEventListener("click", () => setSelectedGame(normalizeGameId(button.dataset.gameId)));
}
powerUpsOpenButton.addEventListener("click", () => openPowerUpsPage());
for (const [kind, button] of Object.entries(powerUpButtons) as Array<[PowerUpKind, HTMLButtonElement]>) {
  button.addEventListener("click", () => setPowerUp(kind, !state.powerUps[kind]));
}

createRoomButton.addEventListener("click", () => {
  if (state.mode === "idle" && state.homePanel !== "host") {
    state.homePanel = "host";
    updateUi();
    return;
  }
  findMatch("host");
});

joinRoomButton.addEventListener("click", () => {
  findMatch("join");
});

singlePlayerButton.addEventListener("click", () => {
  if (state.mode === "idle" && state.homePanel !== "solo") {
    state.homePanel = "solo";
    updateUi();
    return;
  }
  startSinglePlayer();
});

cancelQueueButton.addEventListener("click", () => {
  void cancelMatchmaking();
});

backHomeButton.addEventListener("click", () => {
  state.homePanel = state.homePanel === "powerups" ? state.powerUpsReturn : "menu";
  updateUi();
});

readyButton.addEventListener("click", () => {
  if (state.snapshot?.phase === "playing") {
    state.controlMode = state.controlMode === "shoot" ? "look" : "shoot";
    state.pointer = { type: "idle" };
    updateUi();
    return;
  }
  sendLobbyAction();
});

lobbyStartButton.addEventListener("click", () => {
  sendLobbyAction();
});

restartButton.addEventListener("click", () => {
  if (state.mode === "single") {
    startSinglePlayer();
  } else {
    send({ type: "restart", holeCount: clampHoleCount(Number(holeCountInput.value)), coursePack: state.coursePack, powerUps: state.powerUps });
  }
});

canvas.addEventListener("pointerdown", onPointerDown);
canvas.addEventListener("pointermove", onPointerMove);
canvas.addEventListener("pointerup", onPointerUp);
canvas.addEventListener("pointercancel", onPointerUp);
canvas.addEventListener("wheel", onWheel, { passive: false });
document.addEventListener("touchmove", (event) => event.preventDefault(), { passive: false });
document.addEventListener("gesturestart", (event) => event.preventDefault());
window.addEventListener("resize", resizeCanvas);

document.querySelector("#zoomIn")?.addEventListener("click", () => zoomAt(1.15));
document.querySelector("#zoomOut")?.addEventListener("click", () => zoomAt(0.87));
document.querySelector("#fitCourse")?.addEventListener("click", () => {
  fitCameraToHole();
  state.controlMode = "shoot";
  updateUi();
});

Events.on(state.engine, "collisionStart", (event) => {
  for (const pair of event.pairs) {
    const labels = [pair.bodyA.label, pair.bodyB.label];
    if (labels.includes("ball") && labels.includes("wall")) {
      playTap();
    }
  }
});

resizeCanvas();
loadHole(0);
updateUi();
void refreshMatchStatus();
window.setInterval(() => void refreshMatchStatus(), 3500);
requestAnimationFrame(frame);

function findMatch(intent: "host" | "join"): void {
  const previousSocket = state.socket;
  const previousMatchSocket = state.matchSocket;
  state.socket = undefined;
  state.matchSocket = undefined;
  previousSocket?.close();
  previousMatchSocket?.close();
  state.mode = "matchmaking";
  state.matchIntent = intent;
  state.matchTicket = undefined;
  state.homePanel = "menu";
  state.autoReady = true;
  state.snapshot = undefined;
  state.localSlot = undefined;
  createRoomButton.disabled = true;
  joinRoomButton.disabled = true;
  setActionButton(createRoomButton, intent === "host" ? "Hosting..." : "Host Game", intent === "host" ? "Waiting" : "Open table");
  setActionButton(joinRoomButton, intent === "join" ? "Joining..." : "Join Game", intent === "join" ? "Connecting" : "Enter match");
  setConnection(intent === "host" ? "Hosting" : "Joining");
  showOverlay(intent === "host" ? "Hosting game" : "Joining game", intent === "host" ? "Waiting for a player to join." : "Looking for an open hosted game.");

  const holeCount = clampHoleCount(Number(holeCountInput.value));
  updateUi();
  void createMatchTicket(holeCount, intent);
}

async function refreshMatchStatus(): Promise<void> {
  if (state.mode !== "idle") return;
  try {
    const response = await fetch(`${state.apiOrigin}/api/match/status`);
    if (!response.ok) return;
    const result = (await response.json()) as { waitingHosts?: number };
    state.waitingHosts = Math.max(0, Number(result.waitingHosts ?? 0));
    updateUi();
  } catch {
    state.waitingHosts = 0;
  }
}

async function createMatchTicket(holeCount: number, intent: "host" | "join"): Promise<void> {
  try {
    const response = await fetch(`${state.apiOrigin}/api/match`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId: state.clientId,
        name: state.playerName,
        gameId: state.gameId,
        holeCount,
        playMode: state.playMode,
        coursePack: state.coursePack,
        powerUps: state.powerUps,
        intent,
      }),
    });
    if (!response.ok) throw new Error(`Match request failed: ${response.status}`);
    const result = (await response.json()) as {
      status: "waiting" | "matched" | "no_host";
      ticket?: string;
      code?: string;
      gameId?: GameId;
      holeCount?: number;
      playMode?: PlayMode;
      coursePack?: CoursePack;
      powerUps?: PowerUpSettings;
    };
    if (state.mode !== "matchmaking" || state.matchIntent !== intent) {
      if (result.ticket) {
        void fetch(`${state.apiOrigin}/api/match/${encodeURIComponent(result.ticket)}`, { method: "DELETE" }).catch(() => undefined);
      }
      return;
    }
    if (result.status === "no_host") {
      showOverlay("No hosted game", "Ask the other player to press Host Game, then press Join Game again.");
      setConnection("No host");
      state.mode = "idle";
      state.matchIntent = undefined;
      state.matchTicket = undefined;
      state.waitingHosts = 0;
      updateUi();
      return;
    }
    if (result.status === "matched" && result.code) {
      state.gameId = normalizeGameId(result.gameId ?? state.gameId);
      holeCountInput.value = String(result.holeCount ?? holeCount);
      state.playMode = normalizePlayMode(result.playMode ?? state.playMode);
      state.coursePack = normalizeCoursePack(result.coursePack ?? state.coursePack);
      state.powerUps = normalizePowerUps(result.powerUps ?? state.powerUps);
      state.matchTicket = undefined;
      connectRoom(result.code, { replaceHistory: false, autoReady: true });
      return;
    }

    if (!result.ticket) throw new Error("Missing match ticket");
    state.matchTicket = result.ticket;
    setConnection("Hosting");
    showOverlay("Hosting game", "Waiting for a player to join.");
    pollMatchTicket(result.ticket, holeCount);
  } catch (error) {
    if (state.mode !== "matchmaking" || state.matchIntent !== intent) return;
    console.error(error);
    showOverlay("Matchmaking unavailable", "The realtime Worker is not reachable yet. Try Single Player or try again.");
    setConnection("Queue error");
    state.mode = "idle";
    state.matchIntent = undefined;
    state.matchTicket = undefined;
    updateUi();
  }
}

async function cancelMatchmaking(): Promise<void> {
  if (state.mode !== "matchmaking") return;
  const ticket = state.matchTicket;
  const previousMatchSocket = state.matchSocket;
  state.matchSocket = undefined;
  previousMatchSocket?.close();
  state.mode = "idle";
  state.matchIntent = undefined;
  state.matchTicket = undefined;
  state.autoReady = false;
  state.homePanel = "menu";
  setConnection("Canceled");
  hideOverlay();
  updateUi();

  if (ticket) {
    try {
      await fetch(`${state.apiOrigin}/api/match/${encodeURIComponent(ticket)}`, { method: "DELETE" });
    } catch (error) {
      console.warn("Could not cancel match ticket", error);
    }
  }
  void refreshMatchStatus();
}

function pollMatchTicket(ticket: string, fallbackHoleCount: number): void {
  window.setTimeout(async () => {
    if (state.mode !== "matchmaking") return;

    try {
      const response = await fetch(`${state.apiOrigin}/api/match/${encodeURIComponent(ticket)}`);
      if (!response.ok) throw new Error(`Ticket poll failed: ${response.status}`);
      const result = (await response.json()) as {
        status: "waiting" | "matched" | "expired";
        code?: string;
        gameId?: GameId;
        holeCount?: number;
        playMode?: PlayMode;
        coursePack?: CoursePack;
        powerUps?: PowerUpSettings;
      };
      if (result.status === "matched" && result.code) {
        state.gameId = normalizeGameId(result.gameId ?? state.gameId);
        holeCountInput.value = String(result.holeCount ?? fallbackHoleCount);
        state.playMode = normalizePlayMode(result.playMode ?? state.playMode);
        state.coursePack = normalizeCoursePack(result.coursePack ?? state.coursePack);
        state.powerUps = normalizePowerUps(result.powerUps ?? state.powerUps);
        state.matchTicket = undefined;
        connectRoom(result.code, { replaceHistory: false, autoReady: true });
        return;
      }
      pollMatchTicket(ticket, fallbackHoleCount);
    } catch (error) {
      console.error(error);
      showOverlay("Still waiting", "The waiting room request stalled. Try Host Game again or play Single Player.");
      setConnection("Queue stalled");
      state.mode = "idle";
      state.matchIntent = undefined;
      state.matchTicket = undefined;
      updateUi();
    }
  }, 900);
}

async function createRoom(): Promise<void> {
  createRoomButton.disabled = true;
  setActionButton(createRoomButton, "Hosting...", "Creating table");
  try {
    const response = await fetch(`${state.apiOrigin}/api/rooms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId: state.clientId,
        name: state.playerName,
        gameId: state.gameId,
        holeCount: clampHoleCount(Number(holeCountInput.value)),
        playMode: state.playMode,
        coursePack: state.coursePack,
        powerUps: state.powerUps,
      }),
    });
    if (!response.ok) throw new Error(`Room create failed: ${response.status}`);
    const data = (await response.json()) as { code: string; gameId?: GameId; holeCount: number; playMode?: PlayMode; coursePack?: CoursePack; powerUps?: PowerUpSettings };
    state.gameId = normalizeGameId(data.gameId ?? state.gameId);
    holeCountInput.value = String(data.holeCount);
    state.playMode = normalizePlayMode(data.playMode ?? state.playMode);
    state.coursePack = normalizeCoursePack(data.coursePack ?? state.coursePack);
    state.powerUps = normalizePowerUps(data.powerUps ?? state.powerUps);
    connectRoom(data.code, { replaceHistory: true, autoReady: false });
  } catch (error) {
    showOverlay("Could not create room", "The realtime Worker is not reachable yet. Start or deploy it, then try again.");
    console.error(error);
  } finally {
    createRoomButton.disabled = false;
    setActionButton(createRoomButton, "Find Match", "Open table");
  }
}

function connectRoom(code: string, options: { replaceHistory?: boolean; autoReady?: boolean } = {}): void {
  const clean = code.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (!clean) return;
  const previousSocket = state.socket;
  const previousMatchSocket = state.matchSocket;
  state.socket = undefined;
  state.matchSocket = undefined;
  previousSocket?.close();
  previousMatchSocket?.close();
  state.mode = "multi";
  state.matchIntent = undefined;
  state.matchTicket = undefined;
  state.homePanel = "menu";
  state.waitingHosts = 0;
  state.autoReady = Boolean(options.autoReady);
  const origin = new URL(state.apiOrigin);
  const protocol = origin.protocol === "https:" ? "wss:" : "ws:";
  const wsUrl = `${protocol}//${origin.host}/ws/${clean}?clientId=${encodeURIComponent(state.clientId)}&name=${encodeURIComponent(state.playerName)}`;
  const socket = new WebSocket(wsUrl);
  state.socket = socket;

  socket.addEventListener("open", () => {
    setConnection("Connected");
    if (options.replaceHistory) {
      history.replaceState(null, "", `${location.pathname}?room=${clean}`);
    } else {
      history.replaceState(null, "", location.pathname);
    }
  });

  socket.addEventListener("message", (event) => {
    const message = JSON.parse(event.data as string) as ServerMessage;
    handleServerMessage(message);
  });

  socket.addEventListener("close", () => {
    if (state.socket === socket) setConnection("Disconnected");
    updateUi();
  });

  socket.addEventListener("error", () => {
    setConnection("Connection error");
  });
}

function startSinglePlayer(): void {
  const previousSocket = state.socket;
  const previousMatchSocket = state.matchSocket;
  state.socket = undefined;
  state.matchSocket = undefined;
  previousSocket?.close();
  previousMatchSocket?.close();
  state.mode = "single";
  state.matchIntent = undefined;
  state.homePanel = "menu";
  state.autoReady = false;
  state.localSlot = "A";
  const holeCount = clampHoleCount(Number(holeCountInput.value));
  if (isArcadeGameId(state.gameId)) {
    const player = makeLocalPlayer(holeCount);
    state.snapshot = {
      type: "room_state",
      code: "SOLO",
      config: {
        gameId: state.gameId,
        holeCount,
        playMode: "race",
        coursePack: state.coursePack,
        powerUps: state.powerUps,
        seed: makeClientSeed(`solo-${state.gameId}`),
        holeIds: [],
      },
      hostId: state.clientId,
      players: [player],
      currentHole: 0,
      phase: "playing",
      createdAt: Date.now(),
    };
    history.replaceState(null, "", location.pathname);
    setConnection("Solo");
    prepareArcadeRound();
    updateUi();
    return;
  }
  const coursePack = state.coursePack;
  const round = chooseClientRound("solo", holeCount, coursePack);
  rememberClientRound(round.holeIds, coursePack);
  const player = makeLocalPlayer(holeCount);
  state.snapshot = {
    type: "room_state",
    code: "SOLO",
    config: { gameId: state.gameId, holeCount, playMode: state.playMode, coursePack, powerUps: state.powerUps, seed: round.seed, holeIds: round.holeIds },
    hostId: state.clientId,
    players: [player],
    currentHole: 0,
    phase: "playing",
    createdAt: Date.now(),
  };
  history.replaceState(null, "", location.pathname);
  setConnection("Solo");
  loadHole(0);
  updateUi();
}

function completeSingleHole(strokes: number): void {
  const snapshot = state.snapshot;
  if (!snapshot || state.mode !== "single") return;

  const player = snapshot.players[0];
  player.currentHoleComplete = true;
  player.strokesThisHole = strokes;
  player.scores[snapshot.currentHole] = currentPlayMode() === "race" ? 1 : strokes;

  const nextHole = snapshot.currentHole + 1;
  if (nextHole >= snapshot.config.holeCount) {
    snapshot.phase = "finished";
    updateUi();
    return;
  }

  const previousHole = snapshot.currentHole;
  snapshot.currentHole = nextHole;
  player.currentHoleComplete = false;
  player.strokesThisHole = 0;
  scheduleAdvanceOverlay(previousHole, nextHole);
  updateUi();
}

function handleServerMessage(message: ServerMessage): void {
  switch (message.type) {
    case "room_state": {
      const previousHole = state.snapshot?.currentHole ?? -1;
      const previousPhase = state.snapshot?.phase;
      const previousSeed = state.snapshot?.config.seed;
      state.snapshot = message;
      state.gameId = normalizeGameId(message.config.gameId);
      const local = message.players.find((player) => player.id === state.clientId);
      state.localSlot = local?.slot;
      state.playMode = normalizePlayMode(message.config.playMode);
      state.coursePack = normalizeCoursePack(message.config.coursePack);
      state.powerUps = normalizePowerUps(message.config.powerUps);
      holeCountInput.value = String(message.config.holeCount);

      const waitingForAdvance = state.advanceOverlay?.nextHole === message.currentHole && state.advanceOverlay.until > performance.now();
      const shouldLoadRound =
        !waitingForAdvance &&
        (message.config.seed !== previousSeed || message.currentHole !== previousHole || (previousPhase !== "playing" && message.phase === "playing"));
      if (isArcadeGameId(normalizeGameId(message.config.gameId))) {
        if (shouldLoadRound) prepareArcadeRound();
      } else if (shouldLoadRound) {
        loadHole(message.currentHole);
      }

      if (state.autoReady && message.phase === "lobby" && local && !local.ready) {
        send({ type: "ready" });
      }

      updateUi();
      break;
    }

    case "shot": {
      if (message.playerId === state.clientId || message.hole !== state.localHole) return;
      const player = state.snapshot?.players.find((entry) => entry.id === message.playerId);
      if (player) {
        player.strokesThisHole = Math.max(player.strokesThisHole, message.stroke);
        player.currentHoleComplete = false;
      }
      startGhostShot(message.playerId, message.slot, message.start, message.velocity);
      updateUi();
      break;
    }

    case "ball_state": {
      if (message.playerId === state.clientId || message.hole !== state.localHole) return;
      updateGhostState(message.playerId, message.slot, message.pos, message.vel, message.moving);
      break;
    }

    case "game_event": {
      if (message.playerId === state.clientId) return;
      if (isArcadeGameId(currentGameId()) && isArcadePayload(message.event)) {
        arcadeRuntime.applyRemoteEvent(message.event, message.playerId);
      }
      break;
    }

    case "game_score": {
      applyGameScoreMessage(message);
      updateUi();
      break;
    }

    case "hole_complete": {
      applyHoleCompleteMessage(message);
      updateUi();
      break;
    }

    case "hole_advance": {
      if (message.phase === "playing") {
        scheduleAdvanceOverlay(state.localHole, message.hole);
      }
      updateUi();
      break;
    }

    case "player_joined":
    case "player_left": {
      updateUi();
      break;
    }

    case "error": {
      showOverlay("Room error", message.message);
      break;
    }
  }
}

function applyHoleCompleteMessage(message: Extract<ServerMessage, { type: "hole_complete" }>): void {
  const snapshot = state.snapshot;
  if (!snapshot) return;
  const player = snapshot.players.find((entry) => entry.id === message.playerId);
  if (!player) return;
  player.currentHoleComplete = true;
  player.strokesThisHole = message.strokes;
  player.scores = [...message.scorecard];
}

function scheduleAdvanceOverlay(previousHole: number, nextHole: number): void {
  if (state.advanceOverlay?.nextHole === nextHole && state.advanceOverlay.until > performance.now()) return;
  const snapshot = state.snapshot;
  if (!snapshot) return;
  const playMode = currentPlayMode(snapshot);
  const arcade = isArcadeGameId(currentGameId(snapshot));
  const players = snapshot.players;
  const previousHoleNumber = previousHole + 1;
  const winner = holeWinner(players, previousHole, playMode);
  const title =
    arcade
      ? winner
        ? `${winner.name} wins Round ${previousHoleNumber}`
        : `Round ${previousHoleNumber} tied`
      :
    playMode === "race"
      ? winner
        ? `${winner.name} wins Hole ${previousHoleNumber}`
        : `Hole ${previousHoleNumber} tied`
      : winner
        ? `${winner.name} takes Hole ${previousHoleNumber}`
        : `Hole ${previousHoleNumber} tied`;
  const copy =
    arcade
      ? `Score: ${scoreSummary(players, "race")}`
      : playMode === "race"
      ? `Race score: ${scoreSummary(players, "race")}`
      : `Round score: ${scoreSummary(players, "strokes")}`;
  state.advanceOverlay = {
    previousHole,
    nextHole,
    title,
    copy,
    until: performance.now() + 3300,
  };
}

function send(message: ClientMessage): void {
  if (state.socket?.readyState === WebSocket.OPEN) {
    state.socket.send(JSON.stringify(message));
  }
}

function sendArcadeEvent(event: ArcadeEvent): void {
  if (state.mode !== "multi") return;
  send({ type: "game_event", gameId: event.gameId, event });
}

function updateArcadeScore(score: number, complete = false): void {
  const snapshot = state.snapshot;
  if (!snapshot || !isArcadeGameId(currentGameId(snapshot)) || snapshot.phase !== "playing") return;
  const normalizedScore = Number.isFinite(score) ? Math.max(0, Math.round(score)) : 0;

  if (state.mode === "single") {
    const player = snapshot.players.find((entry) => entry.id === state.clientId);
    if (!player) return;
    player.strokesThisHole = normalizedScore;
    player.scores[snapshot.currentHole] = normalizedScore;
    player.currentHoleComplete = complete;
    if (complete) {
      completeSingleArcadeRound();
    } else {
      updateUi();
    }
    return;
  }

  send({ type: "game_score", hole: snapshot.currentHole, score: normalizedScore, complete });
}

function completeSingleArcadeRound(): void {
  const snapshot = state.snapshot;
  if (!snapshot || state.mode !== "single") return;
  const nextRound = snapshot.currentHole + 1;
  if (nextRound >= snapshot.config.holeCount) {
    snapshot.phase = "finished";
    updateUi();
    return;
  }
  const previousRound = snapshot.currentHole;
  snapshot.currentHole = nextRound;
  const player = snapshot.players[0];
  player.currentHoleComplete = false;
  player.strokesThisHole = 0;
  scheduleAdvanceOverlay(previousRound, nextRound);
  prepareArcadeRound();
  updateUi();
}

function applyGameScoreMessage(message: Extract<ServerMessage, { type: "game_score" }>): void {
  const snapshot = state.snapshot;
  if (!snapshot) return;
  const player = snapshot.players.find((entry) => entry.id === message.playerId);
  if (!player) return;
  player.currentHoleComplete = message.complete;
  player.strokesThisHole = message.score;
  player.scores = [...message.scorecard];
}

function prepareArcadeRound(): void {
  const snapshot = state.snapshot;
  const gameId = currentGameId(snapshot);
  if (!snapshot || !isArcadeGameId(gameId)) return;
  const roundKey = `${gameId}:${snapshot.config.seed}:${snapshot.currentHole}`;
  state.localHole = snapshot.currentHole;
  arcadeRuntime.sync(gameId, snapshot.config.seed, snapshot.currentHole, snapshot.players);
  if (state.arcadeRoundKey === roundKey) return;
  state.arcadeRoundKey = roundKey;
  state.ball = undefined;
  state.ghosts.clear();
  state.dynamicRails = [];
  state.pointer = { type: "idle" };
  Composite.clear(state.engine.world, false);
  scheduleCanvasRefit();
}

function isArcadeActive(snapshot = state.snapshot): boolean {
  return Boolean(snapshot?.phase === "playing" && isArcadeGameId(currentGameId(snapshot)));
}

function isArcadePayload(value: unknown): value is ArcadeEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as { gameId?: unknown; round?: unknown; kind?: unknown };
  return isArcadeGameId(normalizeGameId(event.gameId)) && typeof event.round === "number" && typeof event.kind === "string";
}

function sendLobbyAction(): void {
  if (state.snapshot?.phase === "lobby" && state.snapshot.hostId === state.clientId) {
    send({ type: "start" });
    return;
  }
  send({ type: "ready" });
}

function loadHole(index: number, forcedHole?: HoleDefinition): void {
  state.arcadeRoundKey = "";
  state.currentHole = forcedHole ?? currentRoundHole(index);
  state.localHole = index;
  state.localStrokes = 0;
  state.holeComplete = false;
  state.didReportComplete = false;
  state.pointer = { type: "idle" };
  state.dynamicRails = [];
  state.zoneCooldowns.clear();
  state.airborneBodies.clear();
  Composite.clear(state.engine.world, false);
  state.ghosts.clear();

  const walls = buildCourseBodies(state.currentHole);
  World.add(state.engine.world, walls);
  updateCourseDynamics(courseMotionSeconds());
  const tee = teeForSlot(state.localSlot);
  state.ball = Bodies.circle(tee.x, tee.y, BALL_RADIUS, {
    label: "ball",
    restitution: 0.78,
    frictionAir: 0.021,
    friction: 0.01,
    density: 0.0012,
    collisionFilter: {
      category: BALL_CATEGORY,
      mask: WALL_CATEGORY,
    },
    render: { visible: false },
  });
  World.add(state.engine.world, state.ball);
  fitCameraToHole();
  updateUi();
  scheduleCanvasRefit();
}

function buildCourseBodies(hole: HoleDefinition): Matter.Body[] {
  const bodies: Matter.Body[] = [];

  for (const obstacle of hole.obstacles) {
    if (obstacle.kind === "triangle") {
      bodies.push(triangleBody(obstacle));
    } else {
      const body = railBody(obstacle);
      bodies.push(body);
      if (obstacle.kind === "movingRail") {
        state.dynamicRails.push({ obstacle, body });
      }
    }
  }

  return bodies;
}

function railBody(obstacle: SegmentObstacle | MovingRailObstacle): Matter.Body {
  return Bodies.rectangle(obstacle.x, obstacle.y, obstacle.w, obstacle.h, {
    isStatic: true,
    angle: obstacle.angle ?? 0,
    label: "wall",
    restitution: 0.9,
    collisionFilter: {
      category: WALL_CATEGORY,
      mask: BALL_CATEGORY | GHOST_CATEGORY,
    },
    render: { visible: false },
  });
}

function triangleBody(obstacle: TriangleObstacle): Matter.Body {
  const center = centroid(obstacle.points);
  const relative = obstacle.points.map((point) => ({ x: point.x - center.x, y: point.y - center.y }));
  return Bodies.fromVertices(center.x, center.y, [relative], {
    isStatic: true,
    label: "wall",
    restitution: 0.9,
    collisionFilter: {
      category: WALL_CATEGORY,
      mask: BALL_CATEGORY | GHOST_CATEGORY,
    },
    render: { visible: false },
  });
}

function courseMotionSeconds(now = Date.now()): number {
  const startedAt = state.snapshot?.createdAt;
  return startedAt ? Math.max(0, (now - startedAt) / 1000) : now / 1000;
}

function updateCourseDynamics(seconds: number): void {
  for (const entry of state.dynamicRails) {
    const center = movingRailCenter(entry.obstacle, seconds);
    Body.setPosition(entry.body, center);
    Body.setAngle(entry.body, entry.obstacle.angle ?? 0);
    Body.setVelocity(entry.body, { x: 0, y: 0 });
    Body.setAngularVelocity(entry.body, 0);
  }
}

function frame(now: number): void {
  const delta = Math.min(32, now - state.lastFrameAt);
  state.lastFrameAt = now;

  if (isArcadeActive()) {
    prepareArcadeRound();
    arcadeRuntime.update(now, delta);
    tickAdvanceOverlay(now);
    render();
    publishDebugState();
    requestAnimationFrame(frame);
    return;
  }

  const motionSeconds = courseMotionSeconds();
  updateCourseDynamics(motionSeconds);
  applyCourseForces(motionSeconds);
  const steps = Math.max(1, Math.ceil(delta / 16.667));
  for (let i = 0; i < steps; i += 1) {
    Engine.update(state.engine, delta / steps);
    enforceFairwayBounds();
  }
  settleBodies();
  smoothGhosts(now, delta);
  checkCup();
  sendBallState(now);
  tickAdvanceOverlay(now);
  render();
  publishDebugState();

  requestAnimationFrame(frame);
}

function applyCourseForces(motionSeconds = courseMotionSeconds()): void {
  const movingBodies = [state.ball, ...Array.from(state.ghosts.values()).map((ghost) => ghost.body)].filter(Boolean) as Matter.Body[];
  const powerUps = currentPowerUps();
  for (const body of movingBodies) {
    for (const [zoneIndex, zone] of state.currentHole.zones.entries()) {
      if (isPowerUpZone(zone) && !isPowerUpEnabled(powerUps, zone.kind)) continue;
      if (zone.kind === "rotatingBunker") {
        for (const circle of rotatingBunkerCircles(zone, motionSeconds)) {
          const d = distance(body.position, circle);
          if (d > circle.r) continue;
          Body.setVelocity(body, Vector.mult(body.velocity, 0.956));
          const swirlAngle = Math.atan2(body.position.y - zone.y, body.position.x - zone.x) + Math.PI / 2;
          Body.applyForce(body, body.position, {
            x: Math.cos(swirlAngle) * 0.00008,
            y: Math.sin(swirlAngle) * 0.00008,
          });
        }
        continue;
      }

      const d = distance(body.position, zone);
      if (d > zone.r) continue;

      if (zone.kind === "sand") {
        Body.setVelocity(body, Vector.mult(body.velocity, 0.965));
      } else if (zone.kind === "jump") {
        applyJumpZone(body, zone, zoneIndex, motionSeconds);
      } else if (zone.kind === "bumper") {
        applyBumperZone(body, zone, zoneIndex, motionSeconds);
      } else if (zone.kind === "portal") {
        applyPortalZone(body, zone, zoneIndex, motionSeconds);
      } else {
        const falloff = 1 - d / zone.r;
        const force = zone.force ?? 0.0004;
        Body.applyForce(body, body.position, {
          x: Math.cos(zone.angle ?? 0) * force * falloff,
          y: Math.sin(zone.angle ?? 0) * force * falloff,
        });
      }
    }
  }
}

function applyJumpZone(body: Matter.Body, zone: DiscZone, zoneIndex: number, motionSeconds: number): void {
  const speed = Vector.magnitude(body.velocity);
  if (speed < 1.05 || !triggerZoneCooldown(body, zone, zoneIndex, motionSeconds, 0.72)) return;

  const travel = bodyDirection(body, zone.angle ?? 0);
  const ramp = directionFromAngle(zone.angle ?? Math.atan2(travel.y, travel.x));
  const direction = normalizePoint({
    x: travel.x * 0.65 + ramp.x * 0.35,
    y: travel.y * 0.65 + ramp.y * 0.35,
  });
  const targetSpeed = clamp(speed + (zone.boost ?? 4.4), Math.min(speed * 1.08, MAX_SHOT_SPEED), MAX_SHOT_SPEED * 0.96);
  Body.setVelocity(body, Vector.mult(direction, targetSpeed));
  markAirborne(body, 520);
}

function applyBumperZone(body: Matter.Body, zone: DiscZone, zoneIndex: number, motionSeconds: number): void {
  if (!triggerZoneCooldown(body, zone, zoneIndex, motionSeconds, 0.48)) return;
  const away = normalizePoint({
    x: body.position.x - zone.x,
    y: body.position.y - zone.y,
  }, zone.angle ?? 0);
  const speed = Vector.magnitude(body.velocity);
  const targetSpeed = clamp(Math.max(speed, 3.2) + (zone.strength ?? 5.8), 5.8, MAX_SHOT_SPEED * 0.92);
  Body.setVelocity(body, Vector.mult(away, targetSpeed));
  markAirborne(body, 260);
}

function applyPortalZone(body: Matter.Body, zone: DiscZone, zoneIndex: number, motionSeconds: number): void {
  const portalGroup = zone.group;
  if (!portalGroup || !triggerZoneCooldown(body, zone, zoneIndex, motionSeconds, 0.92)) return;
  const exitIndex = state.currentHole.zones.findIndex((candidate, candidateIndex) => {
    return candidateIndex !== zoneIndex && candidate.kind === "portal" && candidate.group === portalGroup;
  });
  const exit = state.currentHole.zones[exitIndex];
  if (!exit || exit.kind !== "portal") return;

  const travel = bodyDirection(body, exit.angle ?? zone.angle ?? 0);
  const exitDirection = directionFromAngle(exit.angle ?? Math.atan2(travel.y, travel.x));
  const speed = clamp(Vector.magnitude(body.velocity) * (zone.boost ?? 1.08), 3.4, MAX_SHOT_SPEED * 0.9);
  const offset = exit.r + BALL_RADIUS + 10;
  Body.setPosition(body, {
    x: exit.x + exitDirection.x * offset,
    y: exit.y + exitDirection.y * offset,
  });
  Body.setVelocity(body, Vector.mult(exitDirection, speed));
  setZoneCooldown(body, zoneCooldownKey(exit, exitIndex), motionSeconds, 0.92);
  markAirborne(body, 360);
}

function enforceFairwayBounds(): void {
  const bodies = [state.ball, ...Array.from(state.ghosts.values()).map((ghost) => ghost.body)].filter(Boolean) as Matter.Body[];
  for (const body of bodies) {
    enforceFairwayBound(body);
  }
}

function enforceFairwayBound(body: Matter.Body): void {
  const nearest = nearestFairwayEdge(body.position, state.currentHole.fairway);
  const inside = pointInPolygon(body.position, state.currentHole.fairway);
  if (inside && nearest.distance >= BALL_RADIUS) return;

  const target = {
    x: nearest.point.x + nearest.inward.x * BALL_RADIUS,
    y: nearest.point.y + nearest.inward.y * BALL_RADIUS,
  };
  Body.setPosition(body, target);

  const inwardSpeed = body.velocity.x * nearest.inward.x + body.velocity.y * nearest.inward.y;
  if (!inside || inwardSpeed < 0) {
    const reflected = {
      x: body.velocity.x - (1 + WALL_BOUNCE_RESTITUTION) * inwardSpeed * nearest.inward.x,
      y: body.velocity.y - (1 + WALL_BOUNCE_RESTITUTION) * inwardSpeed * nearest.inward.y,
    };
    const tangent = {
      x: reflected.x - (reflected.x * nearest.inward.x + reflected.y * nearest.inward.y) * nearest.inward.x,
      y: reflected.y - (reflected.x * nearest.inward.x + reflected.y * nearest.inward.y) * nearest.inward.y,
    };
    const normal = {
      x: reflected.x - tangent.x,
      y: reflected.y - tangent.y,
    };
    Body.setVelocity(body, {
      x: normal.x + tangent.x * WALL_FRICTION_DAMPING,
      y: normal.y + tangent.y * WALL_FRICTION_DAMPING,
    });
  }
}

function nearestFairwayEdge(point: Point, polygon: Point[]): { point: Point; distance: number; inward: Point } {
  const winding = polygonSignedArea(polygon) >= 0 ? 1 : -1;
  let nearestPoint = polygon[0];
  let nearestInward = { x: 1, y: 0 };
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const candidate = nearestPointOnSegment(point, a, b);
    const d = distance(point, candidate);
    if (d < nearestDistance) {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy) || 1;
      nearestPoint = candidate;
      nearestDistance = d;
      nearestInward = winding > 0 ? { x: -dy / length, y: dx / length } : { x: dy / length, y: -dx / length };
    }
  }

  return { point: nearestPoint, distance: nearestDistance, inward: nearestInward };
}

function triggerZoneCooldown(body: Matter.Body, zone: DiscZone, zoneIndex: number, motionSeconds: number, cooldownSeconds: number): boolean {
  const key = zoneCooldownKey(zone, zoneIndex);
  const cooldowns = cooldownsForBody(body);
  if ((cooldowns.get(key) ?? 0) > motionSeconds) return false;
  cooldowns.set(key, motionSeconds + cooldownSeconds);
  return true;
}

function setZoneCooldown(body: Matter.Body, key: string, motionSeconds: number, cooldownSeconds: number): void {
  cooldownsForBody(body).set(key, motionSeconds + cooldownSeconds);
}

function cooldownsForBody(body: Matter.Body): Map<string, number> {
  let cooldowns = state.zoneCooldowns.get(body.id);
  if (!cooldowns) {
    cooldowns = new Map<string, number>();
    state.zoneCooldowns.set(body.id, cooldowns);
  }
  return cooldowns;
}

function zoneCooldownKey(zone: DiscZone, zoneIndex: number): string {
  return `${state.currentHole.id}:${zone.kind}:${zoneIndex}:${Math.round(zone.x)}:${Math.round(zone.y)}`;
}

function markAirborne(body: Matter.Body, duration: number): void {
  state.airborneBodies.set(body.id, {
    until: performance.now() + duration,
    duration,
  });
}

function isPowerUpZone(zone: DiscZone): zone is DiscZone & { kind: PowerUpKind } {
  return zone.kind === "jump" || zone.kind === "bumper" || zone.kind === "portal";
}

function airborneLift(body: Matter.Body): number {
  const effect = state.airborneBodies.get(body.id);
  if (!effect) return 0;
  const remaining = effect.until - performance.now();
  if (remaining <= 0) {
    state.airborneBodies.delete(body.id);
    return 0;
  }
  const progress = 1 - remaining / effect.duration;
  return Math.sin(clamp(progress, 0, 1) * Math.PI);
}

function bodyDirection(body: Matter.Body, fallbackAngle = 0): Point {
  return normalizePoint(body.velocity, fallbackAngle);
}

function normalizePoint(point: Point, fallbackAngle = 0): Point {
  const magnitude = Math.hypot(point.x, point.y);
  if (magnitude < 0.001) return directionFromAngle(fallbackAngle);
  return { x: point.x / magnitude, y: point.y / magnitude };
}

function directionFromAngle(angle: number): Point {
  return { x: Math.cos(angle), y: Math.sin(angle) };
}

function settleBodies(): void {
  const bodies = [state.ball, ...Array.from(state.ghosts.values()).map((ghost) => ghost.body)].filter(Boolean) as Matter.Body[];
  for (const body of bodies) {
    const speed = Vector.magnitude(body.velocity);
    if (speed < REST_SPEED) {
      Body.setVelocity(body, { x: 0, y: 0 });
      Body.setAngularVelocity(body, 0);
    } else if (speed > MAX_SHOT_SPEED) {
      const normalized = Vector.normalise(body.velocity);
      Body.setVelocity(body, Vector.mult(normalized, MAX_SHOT_SPEED));
    }
  }
}

function smoothGhosts(now: number, delta: number): void {
  const movingBlend = frameBlend(0.24, delta);
  const stoppedBlend = frameBlend(0.38, delta);
  for (const ghost of state.ghosts.values()) {
    const bodySpeed = Vector.magnitude(ghost.body.velocity);
    const target = ghost.moving || bodySpeed > REST_SPEED ? ghost.body.position : ghost.targetPosition;
    const visualGap = distance(ghost.renderPosition, target);

    if (visualGap > GHOST_VISUAL_SNAP_DISTANCE) {
      ghost.renderPosition = copyPoint(target);
      ghost.trail = [copyPoint(target)];
      ghost.lastTrailAt = now;
    } else {
      ghost.renderPosition = blendPoint(ghost.renderPosition, target, ghost.moving ? movingBlend : stoppedBlend);
    }

    const stillSettling = distance(ghost.renderPosition, ghost.targetPosition) > 2.5;
    if (ghost.moving || bodySpeed > REST_SPEED || stillSettling) {
      addGhostTrailPoint(ghost, ghost.renderPosition, now);
    }
  }
}

function addGhostTrailPoint(ghost: Ghost, point: Point, now: number): void {
  if (now - ghost.lastTrailAt < GHOST_TRAIL_INTERVAL) return;
  const last = ghost.trail[ghost.trail.length - 1];
  if (last && distance(last, point) < 2.5) return;
  ghost.trail.push(copyPoint(point));
  ghost.lastTrailAt = now;
  if (ghost.trail.length > 32) ghost.trail.shift();
}

function frameBlend(base: number, delta: number): number {
  return 1 - Math.pow(1 - base, Math.max(0.25, delta / 16.667));
}

function checkCup(): void {
  if (!state.ball || state.holeComplete || state.snapshot?.phase !== "playing") return;
  const cup = state.currentHole.cup;
  const toCup = Vector.sub(cup, state.ball.position);
  const d = Vector.magnitude(toCup);
  const speed = Vector.magnitude(state.ball.velocity);

  if (d < 36 && speed < 7) {
    Body.applyForce(state.ball, state.ball.position, Vector.mult(Vector.normalise(toCup), 0.0008));
  }

  if (d < 17 && speed < 3.2) {
    state.holeComplete = true;
    state.didReportComplete = true;
    Body.setPosition(state.ball, cup);
    Body.setVelocity(state.ball, { x: 0, y: 0 });
    if (state.mode === "single") {
      completeSingleHole(Math.max(1, state.localStrokes));
    } else {
      send({ type: "hole_complete", hole: state.localHole, strokes: Math.max(1, state.localStrokes) });
    }
    updateUi();
  }
}

function sendBallState(now: number): void {
  if (state.mode !== "multi" || !state.ball || state.snapshot?.phase !== "playing") return;
  const moving = Vector.magnitude(state.ball.velocity) > REST_SPEED;
  const shouldSend = moving ? now - state.lastStateSentAt > 90 : now - state.lastStoppedSentAt > 700;
  if (!shouldSend) return;
  state.lastStateSentAt = now;
  if (!moving) state.lastStoppedSentAt = now;
  send({
    type: "ball_state",
    hole: state.localHole,
    pos: { x: state.ball.position.x, y: state.ball.position.y },
    vel: { x: state.ball.velocity.x, y: state.ball.velocity.y },
    moving,
  });
}

function tickAdvanceOverlay(now: number): void {
  const advance = state.advanceOverlay;
  if (!advance) return;
  if (now < advance.until) {
    renderAdvanceOverlay(now);
    return;
  }
  const nextHole = advance.nextHole;
  state.advanceOverlay = undefined;
  if (state.snapshot?.phase === "playing" && state.snapshot.currentHole === nextHole && state.localHole !== nextHole) {
    loadHole(nextHole);
  }
  updateUi();
}

function renderAdvanceOverlay(now = performance.now()): void {
  const advance = state.advanceOverlay;
  if (!advance) return;
  const countdown = Math.max(1, Math.ceil((advance.until - now) / 1000));
  showOverlay(advance.title, `${advance.copy}  Next hole in ${countdown}`);
}

function loadArtImage(src: string): HTMLImageElement {
  const image = new Image();
  image.decoding = "async";
  image.src = src;
  image.addEventListener("load", () => {
    turfPattern = undefined;
    requestAnimationFrame(render);
  });
  return image;
}

function render(): void {
  if (isArcadeActive()) {
    arcadeRuntime.render();
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, rect.width, rect.height);
  drawBackdrop(rect.width, rect.height);

  ctx.save();
  ctx.translate(rect.width / 2, rect.height / 2);
  ctx.scale(state.camera.zoom, state.camera.zoom);
  ctx.translate(-state.camera.x, -state.camera.y);
  drawCourse(state.currentHole);
  drawZones(state.currentHole);
  drawCup(state.currentHole);
  drawObstacles(state.currentHole);
  drawAim();
  drawGhosts();
  drawAimCapture();
  drawBall();
  ctx.restore();
}

function drawBackdrop(width: number, height: number): void {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#263036");
  gradient.addColorStop(0.46, "#b7c0b1");
  gradient.addColorStop(1, "#798575");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  const glow = ctx.createRadialGradient(width * 0.72, height * 0.2, 0, width * 0.72, height * 0.2, Math.max(width, height) * 0.58);
  glow.addColorStop(0, "rgba(255, 224, 134, 0.18)");
  glow.addColorStop(0.42, "rgba(90, 212, 255, 0.08)");
  glow.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = "rgba(255,255,255,0.09)";
  for (let x = -60; x < width + 60; x += 90) {
    for (let y = -60; y < height + 60; y += 90) {
      ctx.beginPath();
      ctx.arc(x, y, 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const vignette = ctx.createRadialGradient(width * 0.5, height * 0.45, Math.min(width, height) * 0.1, width * 0.5, height * 0.45, Math.max(width, height) * 0.72);
  vignette.addColorStop(0, "rgba(0, 0, 0, 0)");
  vignette.addColorStop(1, "rgba(5, 9, 12, 0.28)");
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
}

function drawCourse(hole: HoleDefinition): void {
  ctx.save();
  ctx.lineJoin = "miter";
  ctx.lineCap = "butt";

  ctx.save();
  ctx.translate(0, 12);
  ctx.filter = "blur(10px)";
  drawPath(hole.fairway);
  ctx.fillStyle = "rgba(0, 0, 0, 0.26)";
  ctx.fill();
  ctx.restore();

  drawPath(hole.fairway);
  const base = ctx.createLinearGradient(0, 0, hole.size.w, hole.size.h);
  base.addColorStop(0, "#2ade61");
  base.addColorStop(0.46, "#08b943");
  base.addColorStop(1, "#047d31");
  ctx.fillStyle = base;
  ctx.fill();

  ctx.lineWidth = 58;
  ctx.strokeStyle = "rgba(18, 21, 16, 0.34)";
  ctx.stroke();
  ctx.lineWidth = 48;
  ctx.strokeStyle = "#d6d2bf";
  ctx.stroke();
  ctx.lineWidth = 36;
  ctx.strokeStyle = "#fbfaf0";
  ctx.stroke();
  ctx.lineWidth = 5;
  ctx.strokeStyle = "rgba(12, 60, 28, 0.38)";
  ctx.stroke();

  ctx.clip();
  const pattern = getTurfPattern();
  if (pattern) {
    ctx.globalAlpha = 0.46;
    ctx.fillStyle = pattern;
    ctx.fillRect(-80, -80, hole.size.w + 160, hole.size.h + 160);
    ctx.globalAlpha = 1;
  }
  for (let y = -40; y < hole.size.h + 80; y += 52) {
    ctx.fillStyle = Math.floor(y / 52) % 2 === 0 ? "rgba(255, 255, 255, 0.055)" : "rgba(0, 75, 26, 0.055)";
    ctx.fillRect(-60, y, hole.size.w + 120, 20);
  }
  const highlight = ctx.createRadialGradient(hole.start.x, hole.start.y, 20, hole.start.x, hole.start.y, 260);
  highlight.addColorStop(0, "rgba(255, 255, 210, 0.16)");
  highlight.addColorStop(1, "rgba(255, 255, 210, 0)");
  ctx.fillStyle = highlight;
  ctx.fillRect(0, 0, hole.size.w, hole.size.h);

  const cupGlow = ctx.createRadialGradient(hole.cup.x, hole.cup.y, 10, hole.cup.x, hole.cup.y, 240);
  cupGlow.addColorStop(0, "rgba(86, 224, 255, 0.12)");
  cupGlow.addColorStop(1, "rgba(86, 224, 255, 0)");
  ctx.fillStyle = cupGlow;
  ctx.fillRect(0, 0, hole.size.w, hole.size.h);

  ctx.strokeStyle = "rgba(255, 255, 255, 0.16)";
  ctx.lineWidth = 2;
  for (let y = 72; y < hole.size.h; y += 96) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(hole.size.w, y);
    ctx.stroke();
  }
  ctx.restore();
}

function getTurfPattern(): CanvasPattern | undefined {
  if (!turfImage.complete || turfImage.naturalWidth === 0) return undefined;
  if (!turfPattern) {
    turfPattern = ctx.createPattern(turfImage, "repeat") ?? undefined;
    turfPattern?.setTransform(new DOMMatrix().scale(0.32));
  }
  return turfPattern;
}

function drawZones(hole: HoleDefinition): void {
  ctx.save();
  drawPath(hole.fairway);
  ctx.clip();
  const powerUps = currentPowerUps();
  for (const zone of hole.zones) {
    if (isPowerUpZone(zone) && !isPowerUpEnabled(powerUps, zone.kind)) continue;
    if (zone.kind === "rotatingBunker") {
      drawRotatingBunkerZone(zone);
      continue;
    }
    if (zone.kind === "jump") {
      drawJumpZone(zone);
      continue;
    }
    if (zone.kind === "bumper") {
      drawBumperZone(zone);
      continue;
    }
    if (zone.kind === "portal") {
      drawPortalZone(zone);
      continue;
    }

    ctx.save();
    ctx.beginPath();
    ctx.arc(zone.x, zone.y, zone.r, 0, Math.PI * 2);
    const zoneFill = ctx.createRadialGradient(zone.x - zone.r * 0.28, zone.y - zone.r * 0.35, zone.r * 0.1, zone.x, zone.y, zone.r);
    if (zone.kind === "sand") {
      zoneFill.addColorStop(0, "#fff09a");
      zoneFill.addColorStop(0.58, "#d9b932");
      zoneFill.addColorStop(1, "#a87916");
    } else {
      zoneFill.addColorStop(0, "#fff48c");
      zoneFill.addColorStop(0.62, "#f0ca1a");
      zoneFill.addColorStop(1, "#ab8507");
    }
    ctx.fillStyle = zoneFill;
    ctx.shadowColor = "rgba(0,0,0,0.22)";
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.strokeStyle = "rgba(91, 82, 0, 0.28)";
    ctx.lineWidth = 4;
    ctx.stroke();

    if (zone.kind === "fan") {
      ctx.translate(zone.x, zone.y);
      ctx.rotate(zone.angle ?? 0);
      ctx.fillStyle = "#4b3f02";
      ctx.shadowColor = "rgba(255, 227, 85, 0.34)";
      ctx.shadowBlur = 10;
      for (let i = -1; i <= 1; i += 1) {
        ctx.beginPath();
        ctx.moveTo(-14, i * 13);
        ctx.lineTo(9, i * 13);
        ctx.lineTo(9, i * 13 - 7);
        ctx.lineTo(24, i * 13 + 1);
        ctx.lineTo(9, i * 13 + 9);
        ctx.lineTo(9, i * 13 + 2);
        ctx.lineTo(-14, i * 13 + 2);
        ctx.closePath();
        ctx.fill();
      }
      ctx.shadowColor = "transparent";
    } else {
      ctx.fillStyle = "rgba(95, 69, 2, 0.18)";
      for (let i = 0; i < 9; i += 1) {
        ctx.beginPath();
        ctx.arc(zone.x - 25 + i * 7, zone.y + Math.sin(i) * 18, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }
  ctx.restore();
}

function drawRotatingBunkerZone(zone: HoleDefinition["zones"][number]): void {
  const circles = rotatingBunkerCircles(zone, courseMotionSeconds());
  ctx.save();
  ctx.strokeStyle = "rgba(255, 225, 80, 0.2)";
  ctx.lineWidth = 8;
  ctx.setLineDash([5, 10]);
  ctx.beginPath();
  ctx.arc(zone.x, zone.y, zone.orbitRadius ?? 42, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  for (const circle of circles) {
    ctx.beginPath();
    ctx.moveTo(zone.x, zone.y);
    ctx.lineTo(circle.x, circle.y);
    ctx.strokeStyle = "rgba(255, 237, 140, 0.18)";
    ctx.lineWidth = 5;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(circle.x, circle.y, circle.r, 0, Math.PI * 2);
    const fill = ctx.createRadialGradient(circle.x - circle.r * 0.3, circle.y - circle.r * 0.35, circle.r * 0.12, circle.x, circle.y, circle.r);
    fill.addColorStop(0, "#fff19a");
    fill.addColorStop(0.55, "#d9ad24");
    fill.addColorStop(1, "#89600e");
    ctx.fillStyle = fill;
    ctx.shadowColor = "rgba(0,0,0,0.3)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.strokeStyle = "rgba(65, 48, 5, 0.38)";
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = "rgba(95, 69, 2, 0.2)";
    for (let i = 0; i < 8; i += 1) {
      ctx.beginPath();
      ctx.arc(circle.x - 21 + i * 6, circle.y + Math.sin(i + circle.x) * 14, 2.1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawJumpZone(zone: DiscZone): void {
  const pulse = 0.5 + Math.sin(courseMotionSeconds() * 4.8 + zone.x * 0.01) * 0.5;
  ctx.save();
  ctx.beginPath();
  ctx.arc(zone.x, zone.y, zone.r, 0, Math.PI * 2);
  const fill = ctx.createRadialGradient(zone.x - zone.r * 0.28, zone.y - zone.r * 0.4, zone.r * 0.08, zone.x, zone.y, zone.r);
  fill.addColorStop(0, "#eaffff");
  fill.addColorStop(0.42, "#5ee8ff");
  fill.addColorStop(1, "#096d8f");
  ctx.fillStyle = fill;
  ctx.shadowColor = "rgba(72, 229, 255, 0.36)";
  ctx.shadowBlur = 14 + pulse * 8;
  ctx.shadowOffsetY = 3;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.72)";
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.translate(zone.x, zone.y);
  ctx.rotate(zone.angle ?? 0);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  for (let i = -1; i <= 1; i += 1) {
    const x = i * zone.r * 0.28;
    ctx.beginPath();
    ctx.moveTo(x - zone.r * 0.18, -zone.r * 0.28);
    ctx.lineTo(x + zone.r * 0.2, 0);
    ctx.lineTo(x - zone.r * 0.18, zone.r * 0.28);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.92)";
    ctx.lineWidth = Math.max(4, zone.r * 0.11);
    ctx.stroke();
    ctx.strokeStyle = "rgba(6, 39, 58, 0.3)";
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  ctx.restore();
}

function drawBumperZone(zone: DiscZone): void {
  const pulse = 0.5 + Math.sin(courseMotionSeconds() * 5.4 + zone.y * 0.01) * 0.5;
  ctx.save();
  ctx.beginPath();
  ctx.arc(zone.x, zone.y, zone.r, 0, Math.PI * 2);
  const fill = ctx.createRadialGradient(zone.x - zone.r * 0.3, zone.y - zone.r * 0.36, zone.r * 0.1, zone.x, zone.y, zone.r);
  fill.addColorStop(0, "#fff8df");
  fill.addColorStop(0.38, "#ff8d42");
  fill.addColorStop(1, "#b62634");
  ctx.fillStyle = fill;
  ctx.shadowColor = "rgba(255, 82, 68, 0.32)";
  ctx.shadowBlur = 10 + pulse * 8;
  ctx.shadowOffsetY = 3;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.76)";
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.strokeStyle = "rgba(82, 12, 18, 0.36)";
  ctx.lineWidth = 4;
  for (let ring = 0.34; ring <= 0.78; ring += 0.22) {
    ctx.beginPath();
    ctx.arc(zone.x, zone.y, zone.r * ring, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = "rgba(255, 255, 255, 0.76)";
  for (let i = 0; i < 6; i += 1) {
    const angle = (Math.PI * 2 * i) / 6 + courseMotionSeconds() * 0.8;
    ctx.beginPath();
    ctx.arc(zone.x + Math.cos(angle) * zone.r * 0.58, zone.y + Math.sin(angle) * zone.r * 0.58, Math.max(2.2, zone.r * 0.08), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawPortalZone(zone: DiscZone): void {
  const seconds = courseMotionSeconds();
  const primary = zone.exit === 1 ? "#e889ff" : "#62f0ff";
  const secondary = zone.exit === 1 ? "#5829a6" : "#075a9c";
  const pulse = 0.5 + Math.sin(seconds * 4 + zone.x * 0.01) * 0.5;

  ctx.save();
  ctx.beginPath();
  ctx.arc(zone.x, zone.y, zone.r, 0, Math.PI * 2);
  const fill = ctx.createRadialGradient(zone.x, zone.y, zone.r * 0.16, zone.x, zone.y, zone.r);
  fill.addColorStop(0, "rgba(255, 255, 255, 0.18)");
  fill.addColorStop(0.48, secondary);
  fill.addColorStop(1, "rgba(17, 11, 44, 0.92)");
  ctx.fillStyle = fill;
  ctx.shadowColor = primary;
  ctx.shadowBlur = 12 + pulse * 12;
  ctx.shadowOffsetY = 2;
  ctx.fill();
  ctx.shadowColor = "transparent";

  ctx.translate(zone.x, zone.y);
  ctx.rotate(seconds * (zone.exit === 1 ? -1.2 : 1.2));
  ctx.strokeStyle = primary;
  ctx.lineWidth = 5;
  ctx.setLineDash([zone.r * 0.42, zone.r * 0.24]);
  ctx.beginPath();
  ctx.arc(0, 0, zone.r * 0.72, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.76)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(0, 0, zone.r * 0.42, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = "rgba(255, 255, 255, 0.86)";
  ctx.beginPath();
  ctx.arc(0, 0, Math.max(3, zone.r * 0.1), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawObstacles(hole: HoleDefinition): void {
  ctx.save();
  drawPath(hole.fairway);
  ctx.clip();
  for (const obstacle of hole.obstacles) {
    if (obstacle.kind === "movingRail") {
      const spanX = obstacle.axis === "x" ? obstacle.distance : 0;
      const spanY = obstacle.axis === "y" ? obstacle.distance : 0;
      ctx.save();
      ctx.strokeStyle = "rgba(86, 202, 255, 0.3)";
      ctx.lineWidth = 5;
      ctx.setLineDash([10, 13]);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(obstacle.x - spanX, obstacle.y - spanY);
      ctx.lineTo(obstacle.x + spanX, obstacle.y + spanY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.16)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    }

    ctx.save();
    ctx.fillStyle = "#f7f8f2";
    ctx.strokeStyle = "rgba(20, 43, 28, 0.22)";
    ctx.lineWidth = 4;
    ctx.shadowColor = "rgba(0,0,0,0.24)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    if (obstacle.kind !== "triangle") {
      const center = obstacle.kind === "movingRail" ? movingRailCenter(obstacle, courseMotionSeconds()) : obstacle;
      ctx.translate(center.x, center.y);
      ctx.rotate(obstacle.angle ?? 0);
      roundRect(-obstacle.w / 2, -obstacle.h / 2, obstacle.w, obstacle.h, 4);
      const railFill = ctx.createLinearGradient(0, -obstacle.h / 2, 0, obstacle.h / 2);
      railFill.addColorStop(0, "#ffffff");
      railFill.addColorStop(0.48, "#e6eadf");
      railFill.addColorStop(1, "#b9c0b2");
      ctx.fillStyle = railFill;
      if (obstacle.kind === "movingRail") {
        ctx.shadowColor = "rgba(83, 206, 255, 0.34)";
        ctx.shadowBlur = 16;
      }
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.stroke();
      if (obstacle.kind === "movingRail") {
        ctx.strokeStyle = "#3bbcff";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(-obstacle.w / 2 + 12, 0);
        ctx.lineTo(obstacle.w / 2 - 12, 0);
        ctx.stroke();
        ctx.strokeStyle = "rgba(255, 255, 255, 0.72)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(-obstacle.w / 2 + 16, -obstacle.h * 0.22);
        ctx.lineTo(obstacle.w / 2 - 16, -obstacle.h * 0.22);
        ctx.stroke();
      }
    } else {
      drawPath(obstacle.points);
      ctx.closePath();
      const center = centroid(obstacle.points);
      const stone = ctx.createRadialGradient(center.x - 30, center.y - 30, 10, center.x, center.y, 150);
      stone.addColorStop(0, "#ffffff");
      stone.addColorStop(0.58, "#e1e4da");
      stone.addColorStop(1, "#adb6a8");
      ctx.fillStyle = stone;
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.stroke();
    }
    ctx.restore();
  }
  ctx.restore();
}

function drawCup(hole: HoleDefinition): void {
  const cup = hole.cup;
  ctx.save();
  const cupGlow = ctx.createRadialGradient(cup.x, cup.y, 0, cup.x, cup.y, 70);
  cupGlow.addColorStop(0, "rgba(83, 206, 255, 0.16)");
  cupGlow.addColorStop(1, "rgba(83, 206, 255, 0)");
  ctx.fillStyle = cupGlow;
  ctx.beginPath();
  ctx.arc(cup.x, cup.y, 70, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "#f4f6ef";
  ctx.lineWidth = 3;
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.beginPath();
  ctx.moveTo(cup.x, cup.y - 4);
  ctx.lineTo(cup.x, cup.y - 64);
  ctx.stroke();
  ctx.shadowColor = "transparent";
  ctx.fillStyle = "#e52727";
  ctx.beginPath();
  ctx.moveTo(cup.x + 1, cup.y - 64);
  ctx.lineTo(cup.x + 45, cup.y - 51);
  ctx.lineTo(cup.x + 1, cup.y - 38);
  ctx.closePath();
  ctx.shadowColor = "rgba(0,0,0,0.2)";
  ctx.shadowBlur = 5;
  ctx.shadowOffsetY = 2;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.beginPath();
  ctx.ellipse(cup.x, cup.y, 16, 12, 0, 0, Math.PI * 2);
  const cupFill = ctx.createRadialGradient(cup.x - 5, cup.y - 5, 2, cup.x, cup.y, 18);
  cupFill.addColorStop(0, "#465052");
  cupFill.addColorStop(0.42, "#111516");
  cupFill.addColorStop(1, "#020303");
  ctx.fillStyle = cupFill;
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.42)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cup.x - 3, cup.y - 2, 5, 3, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  ctx.fill();
  ctx.restore();
}

function drawAim(): void {
  if (state.pointer.type !== "aim" || !state.ball || state.snapshot?.phase !== "playing") return;
  const aim = aimVector(state.pointer);
  const speed = shotSpeedFromPower(aim.power);
  if (speed < 0.8) return;
  const dir = aim.direction;
  const powerRatio = clamp(speed / MAX_SHOT_SPEED, 0, 1);
  const lineLength = aimLineLength(speed);
  const end = Vector.add(state.ball.position, Vector.mult(dir, lineLength));

  ctx.save();
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 9;
  ctx.setLineDash([5, 14]);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(state.ball.position.x, state.ball.position.y);
  ctx.lineTo(end.x, end.y);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.strokeStyle = "rgba(5, 36, 16, 0.42)";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(state.pointer.start.x, state.pointer.start.y);
  ctx.lineTo(state.pointer.current.x, state.pointer.current.y);
  ctx.stroke();

  ctx.fillStyle = "rgba(243, 212, 55, 0.92)";
  ctx.strokeStyle = "rgba(85, 76, 7, 0.42)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(state.pointer.start.x, state.pointer.start.y, 18 + powerRatio * 14, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(state.pointer.current.x, state.pointer.current.y, 10, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawGhosts(): void {
  for (const ghost of state.ghosts.values()) {
    const color = SLOT_COLORS[ghost.slot];
    const lift = airborneLift(ghost.body);
    const ghostRadius = BALL_RADIUS + 2 + lift * 5;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 7;
    ctx.globalAlpha = 0.18;
    ctx.shadowColor = color;
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ghost.trail.forEach((point, index) => {
      if (index === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    });
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.lineWidth = 3;
    ctx.globalAlpha = 0.42;
    ctx.stroke();
    ctx.globalAlpha = 0.64;
    ctx.beginPath();
    ctx.arc(ghost.renderPosition.x, ghost.renderPosition.y, ghostRadius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.stroke();
    ctx.restore();
  }
}

function drawAimCapture(): void {
  if (!state.ball || !canShoot() || state.controlMode !== "shoot" || state.pointer.type === "aim") return;
  const radius = AIM_CAPTURE_SCREEN_RADIUS / state.camera.zoom;
  const lineWidth = 2.5 / state.camera.zoom;
  ctx.save();
  ctx.beginPath();
  ctx.arc(state.ball.position.x, state.ball.position.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.07)";
  ctx.fill();
  ctx.setLineDash([7 / state.camera.zoom, 8 / state.camera.zoom]);
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = "rgba(255,255,255,0.86)";
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(state.ball.position.x, state.ball.position.y, radius + 4 / state.camera.zoom, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(17, 35, 23, 0.24)";
  ctx.lineWidth = 1.5 / state.camera.zoom;
  ctx.stroke();
  ctx.restore();
}

function drawBall(): void {
  if (!state.ball) return;
  const color = state.localSlot === "B" ? "#6de5ff" : "#ffffff";
  const lift = airborneLift(state.ball);
  const radius = BALL_RADIUS + lift * 4.5;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.34)";
  ctx.shadowBlur = 8 + lift * 12;
  ctx.shadowOffsetY = 3 + lift * 7;
  ctx.beginPath();
  ctx.arc(state.ball.position.x, state.ball.position.y, radius, 0, Math.PI * 2);
  const ballFill = ctx.createRadialGradient(
    state.ball.position.x - radius * 0.35,
    state.ball.position.y - radius * 0.45,
    radius * 0.1,
    state.ball.position.x,
    state.ball.position.y,
    radius,
  );
  if (state.localSlot === "B") {
    ballFill.addColorStop(0, "#effcff");
    ballFill.addColorStop(0.42, color);
    ballFill.addColorStop(1, "#1f85a3");
  } else {
    ballFill.addColorStop(0, "#ffffff");
    ballFill.addColorStop(0.58, "#f3f5ef");
    ballFill.addColorStop(1, "#b9c0ba");
  }
  ctx.fillStyle = ballFill;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.strokeStyle = "rgba(16, 31, 24, 0.45)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = state.localSlot === "B" ? "rgba(19, 91, 112, 0.22)" : "rgba(130, 139, 134, 0.24)";
  for (let row = -1; row <= 1; row += 1) {
    for (let col = -1; col <= 1; col += 1) {
      if (Math.abs(row) + Math.abs(col) > 2) continue;
      ctx.beginPath();
      ctx.arc(state.ball.position.x + col * 5, state.ball.position.y + row * 4, 1.1 + lift * 0.25, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.beginPath();
  ctx.arc(state.ball.position.x - 4, state.ball.position.y - 5, 3.5 + lift * 0.8, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fill();
  ctx.restore();
}

function startGhostShot(playerId: string, slot: PlayerSlot, start: Point, velocity: Point): void {
  let ghost = state.ghosts.get(playerId);
  const now = performance.now();
  const previousRender = ghost?.renderPosition;
  if (!ghost) {
    const body = Bodies.circle(start.x, start.y, BALL_RADIUS, {
      label: "ghost",
      restitution: 0.78,
      frictionAir: 0.021,
      density: 0.0012,
      collisionFilter: {
        category: GHOST_CATEGORY,
        mask: WALL_CATEGORY,
      },
      render: { visible: false },
    });
    ghost = {
      slot,
      body,
      trail: [copyPoint(start)],
      moving: true,
      lastSeen: now,
      lastTrailAt: now,
      renderPosition: copyPoint(start),
      targetPosition: copyPoint(start),
      targetVelocity: copyPoint(velocity),
    };
    state.ghosts.set(playerId, ghost);
    World.add(state.engine.world, body);
  }
  Body.setPosition(ghost.body, start);
  Body.setVelocity(ghost.body, velocity);
  ghost.slot = slot;
  ghost.targetPosition = copyPoint(start);
  ghost.targetVelocity = copyPoint(velocity);
  if (!previousRender || distance(previousRender, start) > GHOST_VISUAL_SNAP_DISTANCE) {
    ghost.renderPosition = copyPoint(start);
  }
  ghost.trail = [copyPoint(ghost.renderPosition)];
  ghost.moving = true;
  ghost.lastSeen = now;
  ghost.lastTrailAt = now;
}

function updateGhostState(playerId: string, slot: PlayerSlot, pos: Point, vel: Point, moving: boolean): void {
  let ghost = state.ghosts.get(playerId);
  if (!ghost) {
    startGhostShot(playerId, slot, pos, vel);
    ghost = state.ghosts.get(playerId);
    if (!ghost) return;
  }
  const now = performance.now();
  const current = ghost.body.position;
  const error = { x: pos.x - current.x, y: pos.y - current.y };
  const errorDistance = Math.hypot(error.x, error.y);

  ghost.slot = slot;
  ghost.targetPosition = copyPoint(pos);
  ghost.targetVelocity = copyPoint(vel);

  if (!moving || errorDistance > GHOST_BODY_SNAP_DISTANCE) {
    Body.setPosition(ghost.body, pos);
    Body.setVelocity(ghost.body, vel);
  } else {
    Body.setVelocity(ghost.body, {
      x: vel.x + error.x * GHOST_BODY_CORRECTION,
      y: vel.y + error.y * GHOST_BODY_CORRECTION,
    });
  }

  if (distance(ghost.renderPosition, pos) > GHOST_VISUAL_SNAP_DISTANCE) {
    ghost.renderPosition = copyPoint(pos);
    ghost.trail = [copyPoint(pos)];
    ghost.lastTrailAt = now;
  }

  ghost.moving = moving;
  ghost.lastSeen = now;
}

function onPointerDown(event: PointerEvent): void {
  if (isArcadeActive()) {
    canvas.setPointerCapture(event.pointerId);
    arcadeRuntime.onPointerDown(event);
    return;
  }
  event.preventDefault();
  canvas.setPointerCapture(event.pointerId);
  const world = screenToWorld(event);
  const screen = { x: event.clientX, y: event.clientY };
  if (canShoot() && state.controlMode === "shoot" && isInsideAimCapture(screen)) {
    const ball = state.ball?.position ?? world;
    const ballWorld = { x: ball.x, y: ball.y };
    const ballScreen = worldToScreen(ballWorld);
    state.pointer = {
      type: "aim",
      id: event.pointerId,
      start: ballWorld,
      current: world,
      screenStart: ballScreen,
      screenCurrent: screen,
    };
  } else {
    state.pointer = { type: "pan", id: event.pointerId, lastScreen: screen };
  }
}

function onPointerMove(event: PointerEvent): void {
  if (isArcadeActive()) {
    arcadeRuntime.onPointerMove(event);
    return;
  }
  event.preventDefault();
  if (state.pointer.type === "aim" && state.pointer.id === event.pointerId) {
    state.pointer.current = screenToWorld(event);
    state.pointer.screenCurrent = { x: event.clientX, y: event.clientY };
  } else if (state.pointer.type === "pan" && state.pointer.id === event.pointerId) {
    const dx = (event.clientX - state.pointer.lastScreen.x) / state.camera.zoom;
    const dy = (event.clientY - state.pointer.lastScreen.y) / state.camera.zoom;
    state.camera.x -= dx;
    state.camera.y -= dy;
    state.pointer.lastScreen = { x: event.clientX, y: event.clientY };
  }
}

function onPointerUp(event: PointerEvent): void {
  if (isArcadeActive()) {
    arcadeRuntime.onPointerUp(event);
    return;
  }
  event.preventDefault();
  if (state.pointer.type === "aim" && state.pointer.id === event.pointerId && state.ball && canShoot()) {
    state.pointer.current = screenToWorld(event);
    state.pointer.screenCurrent = { x: event.clientX, y: event.clientY };
    const aim = aimVector(state.pointer);
    if (aim.power > 10) {
      const direction = aim.direction;
      const speed = shotSpeedFromPower(aim.power);
      const velocity = Vector.mult(direction, speed);
      Body.setVelocity(state.ball, velocity);
      state.localStrokes += 1;
      state.holeComplete = false;
      playTap(0.08);
      send({
        type: "shot",
        hole: state.localHole,
        stroke: state.localStrokes,
        start: { x: state.ball.position.x, y: state.ball.position.y },
        velocity: { x: velocity.x, y: velocity.y },
        shotId: `${state.clientId}-${makeId()}`,
      });
      updateUi();
    }
  }
  state.pointer = { type: "idle" };
}

function aimVector(pointer: Extract<PointerMode, { type: "aim" }>): { direction: Point; power: number } {
  const pull = {
    x: pointer.screenStart.x - pointer.screenCurrent.x,
    y: pointer.screenStart.y - pointer.screenCurrent.y,
  };
  const power = Math.hypot(pull.x, pull.y);
  if (power < 0.001) {
    return { direction: { x: 1, y: 0 }, power: 0 };
  }
  return {
    direction: { x: pull.x / power, y: pull.y / power },
    power,
  };
}

function shotSpeedFromPower(power: number): number {
  const ratio = clamp(power / FULL_PULL_POWER, 0, 1);
  return MAX_SHOT_SPEED * Math.pow(ratio, SHOT_POWER_EXPONENT);
}

function aimLineLength(speed: number): number {
  return 44 + clamp(speed / MAX_SHOT_SPEED, 0, 1) * 250;
}

function onWheel(event: WheelEvent): void {
  event.preventDefault();
  zoomAt(event.deltaY < 0 ? 1.08 : 0.92);
}

function canShoot(): boolean {
  if (isArcadeActive()) return false;
  return Boolean(
      state.snapshot?.phase === "playing" &&
      state.ball &&
      !state.advanceOverlay &&
      !state.holeComplete &&
      Vector.magnitude(state.ball.velocity) <= REST_SPEED,
  );
}

function screenToWorld(event: PointerEvent): Point {
  const rect = canvas.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  return {
    x: (x - rect.width / 2) / state.camera.zoom + state.camera.x,
    y: (y - rect.height / 2) / state.camera.zoom + state.camera.y,
  };
}

function worldToScreen(point: Point): Point {
  const rect = canvas.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2 + (point.x - state.camera.x) * state.camera.zoom,
    y: rect.top + rect.height / 2 + (point.y - state.camera.y) * state.camera.zoom,
  };
}

function isInsideAimCapture(screen: Point): boolean {
  if (!state.ball) return false;
  return distance(screen, worldToScreen(state.ball.position)) <= AIM_CAPTURE_SCREEN_RADIUS;
}

function teeForSlot(slot: PlayerSlot | undefined): Point {
  const base = state.currentHole.start;
  if (state.mode !== "multi" || !slot) return base;
  const offset = SLOT_OFFSETS[slot];
  return { x: base.x + offset.x, y: base.y + offset.y };
}

function fitCameraToHole(): void {
  const rect = canvas.getBoundingClientRect();
  const padding = rect.width < 620 ? 40 : 90;
  const targetZoom = Math.min((rect.width - padding) / state.currentHole.size.w, (rect.height - padding) / state.currentHole.size.h);
  const isLongCourse = currentCoursePack() === "long";
  const minZoom = isLongCourse ? 0.18 : 0.32;
  const zoom = clamp(targetZoom, minZoom, 1.4);
  state.camera = {
    x: state.currentHole.size.w / 2,
    y: state.currentHole.size.h / 2,
    zoom,
  };

  if (isLongCourse && targetZoom < minZoom) {
    const tee = teeForSlot(state.localSlot);
    const toCup = {
      x: state.currentHole.cup.x - tee.x,
      y: state.currentHole.cup.y - tee.y,
    };
    const distanceToCup = Math.hypot(toCup.x, toCup.y) || 1;
    const direction = { x: toCup.x / distanceToCup, y: toCup.y / distanceToCup };
    const viewW = rect.width / zoom;
    const viewH = rect.height / zoom;
    state.camera.x = clampCameraAxis(tee.x + direction.x * viewW * 0.26, viewW, state.currentHole.size.w);
    state.camera.y = clampCameraAxis(tee.y + direction.y * viewH * 0.2, viewH, state.currentHole.size.h);
  }
}

function clampCameraAxis(value: number, viewSpan: number, courseSpan: number): number {
  const margin = viewSpan * 0.42;
  if (courseSpan <= margin * 2) return courseSpan / 2;
  return clamp(value, margin, courseSpan - margin);
}

function zoomAt(factor: number): void {
  const minZoom = currentCoursePack() === "long" ? 0.16 : 0.38;
  state.camera.zoom = clamp(state.camera.zoom * factor, minZoom, 2.3);
}

function resizeCanvas(): void {
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(rect.width * dpr));
  canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  if (!isArcadeActive()) {
    fitCameraToHole();
  }
}

function scheduleCanvasRefit(): void {
  if (pendingCanvasRefit !== undefined) cancelAnimationFrame(pendingCanvasRefit);
  pendingCanvasRefit = requestAnimationFrame(() => {
    pendingCanvasRefit = undefined;
    resizeCanvas();
  });
}

function updateUi(): void {
  const snapshot = state.snapshot;
  document.body.dataset.mode = state.mode;
  document.body.dataset.phase = snapshot?.phase ?? (state.mode === "matchmaking" ? "matchmaking" : "idle");
  document.body.dataset.home = state.homePanel;
  document.body.dataset.control = state.controlMode;
  document.body.dataset.gameId = currentGameId(snapshot);
  document.body.dataset.playMode = currentPlayMode(snapshot);
  document.body.dataset.coursePack = currentCoursePack(snapshot);
  document.body.dataset.powerUps = powerUpsStateLabel(currentPowerUps(snapshot));
  document.body.dataset.waiting = state.waitingHosts > 0 ? "yes" : "no";
  const players = snapshot?.players ?? [];
  const local = players.find((player) => player.id === state.clientId);
  const opponent = players.find((player) => player.id !== state.clientId);
  const holeCount = snapshot?.config.holeCount ?? clampHoleCount(Number(holeCountInput.value));
  const currentHoleNumber = (snapshot?.currentHole ?? 0) + 1;
  const currentHole = currentRoundHole(snapshot?.currentHole ?? 0, snapshot);
  const playerLimit = state.mode === "single" ? 1 : MAX_PLAYERS;
  const activeGameId = currentGameId(snapshot);
  const activeGameIsArcade = isArcadeGameId(activeGameId);
  const playMode = currentPlayMode(snapshot);
  const selectedGame = gameById(state.gameId);
  const selectedGamePlayable = isPlayableGame(state.gameId);
  const connectedPlayers = players.filter((player) => player.connected);
  const isHost = snapshot?.hostId === state.clientId;
  const canHostStart = Boolean(snapshot?.phase === "lobby" && isHost && connectedPlayers.length >= 2);

  setText("#roomCode", modeLabel(snapshot));
  setText("#holeMetaLabel", activeGameIsArcade ? "Round" : "Hole");
  setText("#parMetaLabel", activeGameIsArcade ? "Score" : "Par");
  setText("#holeLabel", `${Math.min(currentHoleNumber, holeCount)}/${holeCount}`);
  setText("#parLabel", activeGameIsArcade ? "Pts" : String(currentHole.par));
  setText("#playerCount", `${connectedPlayers.length}/${playerLimit}`);

  const playersEl = document.querySelector("#players");
  if (playersEl) {
    playersEl.innerHTML = players
      .map((player) => {
        const total = player.scores.reduce((sum, score) => sum + (score || 0), 0);
        const status = player.connected ? (player.ready ? "ready" : "online") : "away";
        return `
          <div class="player-row ${player.id === state.clientId ? "me" : ""}">
            <span class="player-dot" style="background:${player.color}"></span>
            <div>
              <strong>${escapeHtml(player.name)}</strong>
              <span>${playerRoleLabel(player.slot)} · ${status}</span>
            </div>
            <b>${total || (playMode === "race" ? "0" : "-")}</b>
          </div>
        `;
      })
      .join("");
  }

  renderPlayScoreboard(snapshot);
  renderScorecard(snapshot);

  readyButton.disabled = state.mode !== "multi" && !(snapshot?.phase === "playing");
  if (snapshot?.phase === "playing" && activeGameIsArcade) {
    readyButton.textContent = "Live";
    readyButton.disabled = true;
  } else if (snapshot?.phase === "playing") {
    readyButton.textContent = state.controlMode === "shoot" ? "Look" : "Shoot";
  } else if (snapshot?.phase === "lobby" && isHost) {
    readyButton.textContent = canHostStart ? "Start" : "Waiting";
    readyButton.disabled = !canHostStart;
  } else {
    readyButton.textContent = state.mode === "matchmaking" ? "Waiting" : local?.ready ? "Ready" : "Ready";
    readyButton.disabled = state.mode !== "multi" || !snapshot || snapshot.phase !== "lobby" || Boolean(local?.ready);
  }
  restartButton.disabled = !snapshot || (state.mode !== "single" && snapshot.hostId !== state.clientId);
  createRoomButton.disabled = state.mode === "matchmaking" || !selectedGamePlayable;
  joinRoomButton.disabled = state.mode === "matchmaking";
  cancelQueueButton.disabled = state.mode !== "matchmaking";
  if (snapshot?.phase === "lobby") {
    lobbyStartButton.textContent = isHost ? (canHostStart ? "Start Match" : "Waiting for Player") : local?.ready ? "Ready" : "Ready Up";
    lobbyStartButton.disabled = isHost ? !canHostStart : Boolean(local?.ready);
  } else {
    lobbyStartButton.textContent = "Start Match";
    lobbyStartButton.disabled = true;
  }
  setActionButton(
    createRoomButton,
    state.mode === "matchmaking" && state.matchIntent === "host"
      ? "Hosting..."
      : !selectedGamePlayable
        ? "Coming Soon"
        : state.mode === "idle" && state.homePanel === "host"
          ? "Start Hosting"
          : "Host Game",
    !selectedGamePlayable ? selectedGame.shortTitle : state.mode === "idle" && state.homePanel === "host" ? "Use settings" : "Open table",
  );
  setActionButton(
    joinRoomButton,
    state.mode === "matchmaking" && state.matchIntent === "join" ? "Joining..." : state.waitingHosts > 0 ? "Join Hosted Game" : "Join Game",
    state.waitingHosts > 0 ? "Player is waiting" : "Enter match",
  );
  joinRoomButton.classList.toggle("join-available", state.mode === "idle" && state.waitingHosts > 0);
  singlePlayerButton.disabled = state.mode === "matchmaking" || !selectedGamePlayable;
  setActionButton(
    singlePlayerButton,
    !selectedGamePlayable ? "Coming Soon" : state.mode === "idle" && state.homePanel === "solo" ? "Start Solo" : "Single Player",
    !selectedGamePlayable ? selectedGame.shortTitle : state.homePanel === "solo" ? "Launch round" : "Practice run",
  );
  backHomeButton.disabled = state.mode === "matchmaking";
  modeStrokesButton.classList.toggle("active", state.playMode === "strokes");
  modeRaceButton.classList.toggle("active", state.playMode === "race");
  packClassicButton.classList.toggle("active", state.coursePack === "classic");
  packSpecialButton.classList.toggle("active", state.coursePack === "special");
  packLongButton.classList.toggle("active", state.coursePack === "long");
  modeStrokesButton.disabled = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  modeRaceButton.disabled = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  packClassicButton.disabled = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  packSpecialButton.disabled = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  packLongButton.disabled = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  for (const button of gameButtons) {
    const buttonGame = normalizeGameId(button.dataset.gameId);
    const active = state.gameId === buttonGame;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
    button.disabled = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  }
  const settingsLocked = Boolean(snapshot && snapshot.phase === "playing") || state.mode === "matchmaking";
  powerUpsOpenButton.disabled = settingsLocked;
  powerUpsSummary.textContent = powerUpsSummaryText(state.powerUps);
  for (const [kind, button] of Object.entries(powerUpButtons) as Array<[PowerUpKind, HTMLButtonElement]>) {
    const active = isPowerUpEnabled(state.powerUps, kind);
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
    button.disabled = settingsLocked;
  }

  if (state.advanceOverlay && state.advanceOverlay.until > performance.now()) {
    renderAdvanceOverlay();
  } else if (state.mode === "matchmaking") {
    showOverlay(state.matchIntent === "join" ? "Joining game" : "Hosting game", state.matchIntent === "join" ? "Looking for an open hosted game." : "Waiting for a player to join.");
  } else if (!snapshot) {
    showOverlay("Choose a mode", "Host a game, join an open game, or start a local round.");
  } else if (snapshot.phase === "lobby") {
    const waitingText = opponent
      ? isHost
        ? "Press Start now, or wait for up to 4 players."
        : "Waiting for the host to start. Up to 4 players can join."
      : "Waiting for another player.";
    showOverlay("Match found", waitingText);
  } else if (snapshot.phase === "finished") {
    const standings = sortStandings(players, playMode);
    const winner = standings[0];
    const arcadeFinished = isArcadeGameId(currentGameId(snapshot));
    const winnerCopy =
      arcadeFinished
        ? winner
          ? `${winner.name} finishes with ${sumScores(winner.scores)} points.`
          : "Start another round when ready."
        : playMode === "race"
        ? winner
          ? `${winner.name} wins ${sumScores(winner.scores)} of ${holeCount} holes.`
          : "Start another round when ready."
        : winner
          ? `${winner.name} finishes at ${sumScores(winner.scores)} over ${holeCount} holes.`
          : "Start another round when ready.";
    const winnerTitle = arcadeFinished ? `${gameById(currentGameId(snapshot)).shortTitle} complete` : state.mode === "single" ? "Round complete" : playMode === "race" ? "Race complete" : "Match complete";
    resultsTitle.textContent = winnerTitle;
    resultsCopy.textContent = winnerCopy;
    showOverlay(winnerTitle, winnerCopy);
  } else if (state.holeComplete) {
    showOverlay("In the cup", state.mode === "single" ? "Loading the next hole." : "Waiting for the other player to finish this hole.");
  } else {
    hideOverlay();
  }

  if (state.mode === "single") {
    setConnection("Solo");
  } else if (snapshot) {
    setConnection(state.socket?.readyState === WebSocket.OPEN ? "Connected" : "Disconnected");
  } else if (state.mode === "idle") {
    setConnection(state.waitingHosts > 0 && state.homePanel === "menu" ? "Open Game" : "Offline");
  }

  const selectedGameIsArcade = isArcadeGameId(state.gameId);
  const settingsSummary = selectedGameIsArcade
    ? `${selectedGame.shortTitle} · ${holeCount} round${holeCount === 1 ? "" : "s"}`
    : `${selectedGame.shortTitle} · ${coursePackLabel(state.coursePack)} · ${holeCount} hole${holeCount === 1 ? "" : "s"} · ${powerUpsSummaryText(state.powerUps)}`;
  queueTitle.textContent = state.matchIntent === "join" ? "Joining hosted game" : "Waiting for player";
  queueCopy.textContent =
    state.matchIntent === "join"
      ? "Connecting to the open table. The host's settings will load automatically."
      : "Keep this screen open. Players who tap Join Hosted Game enter this match. Up to 4 can play.";
  queueSummary.textContent = state.matchIntent === "join" ? "Using host settings" : settingsSummary;
  cancelQueueButton.textContent = state.matchIntent === "join" ? "Cancel Join" : "Cancel Hosting";

  homeStatus.textContent =
    snapshot?.phase === "lobby"
      ? isHost
        ? `${connectedPlayers.length}/${MAX_PLAYERS} players in lobby. Start now or wait for more.`
        : "You are in. Waiting for the host to start."
      : state.mode === "matchmaking"
      ? state.matchIntent === "join"
        ? "Connecting to the hosted game."
        : "Waiting for someone to join."
      : state.waitingHosts > 0 && state.homePanel === "menu"
        ? "Someone is hosting right now. Up to 4 can play."
        : !selectedGamePlayable
          ? `${selectedGame.title} is queued for the arcade framework. Mini Golf is playable now.`
        : state.homePanel === "host"
          ? "Choose settings, then start hosting."
          : state.homePanel === "solo"
            ? "Choose settings, then start a solo round."
            : state.homePanel === "powerups"
              ? "Toggle course gadgets for solo and hosted games."
              : "Host, join, or play solo.";
  shareHint.textContent =
    snapshot?.phase === "lobby"
      ? isHost
        ? "Start Match launches with everyone currently in the lobby."
        : "The host controls when the round begins."
      : state.mode === "matchmaking"
      ? state.matchIntent === "join"
        ? "Joining uses the host's hole count, mode, courses, and power-up settings."
        : `${settingsSummary}. Host can start once 2+ players join; up to 4 can join.`
      : state.homePanel === "host"
      ? selectedGameIsArcade
        ? `${selectedGame.title}: ${holeCount} live round${holeCount === 1 ? "" : "s"}.`
        : `${selectedGame.title}: ${coursePackLabel(state.coursePack)} courses, ${powerUpsSentence(state.powerUps)}.`
      : state.homePanel === "solo"
        ? selectedGameIsArcade
          ? `${selectedGame.title}: ${holeCount} solo round${holeCount === 1 ? "" : "s"}.`
          : `${selectedGame.title}: ${coursePackLabel(state.coursePack)} courses, ${currentPlayMode()} scoring, ${powerUpsSentence(state.powerUps)}.`
      : state.waitingHosts > 0
        ? "A hosted game is waiting now. Join Hosted Game will use that host's settings."
        : "No hosted game is waiting yet. Host creates one for the next player.";
}

function renderPlayScoreboard(snapshot: RoomSnapshot | undefined): void {
  if (!snapshot || snapshot.phase !== "playing") {
    playScoreboard.innerHTML = "";
    return;
  }

  const playMode = currentPlayMode(snapshot);
  const arcade = isArcadeGameId(currentGameId(snapshot));
  playScoreboard.innerHTML = orderedRoomPlayers(snapshot)
    .map((player) => {
      const strokes = liveStrokesFor(player, snapshot);
      const total = sumScores(player.scores);
      const strokeLabel = arcade ? "pts" : player.currentHoleComplete ? "In cup" : strokes === 1 ? "Shot" : "Shots";
      const totalLabel = arcade ? "total" : playMode === "race" ? "wins" : "total";
      return `
        <div class="live-player-card ${player.id === state.clientId ? "me" : ""} ${player.currentHoleComplete ? "complete" : ""}" style="--player-color:${player.color}">
          <span class="live-player-dot" aria-hidden="true"></span>
          <span class="live-player-name">${escapeHtml(player.name)}</span>
          <span class="live-player-strokes">
            <b>${strokes}</b>
            <small>${strokeLabel}</small>
          </span>
          <span class="live-player-total">${totalLabel} ${total}</span>
        </div>
      `;
    })
    .join("");
}

function orderedRoomPlayers(snapshot: RoomSnapshot): PlayerStatus[] {
  if (state.mode === "single") return snapshot.players.slice(0, 1);
  return PLAYER_SLOTS.map((slot) => snapshot.players.find((player) => player.slot === slot)).filter((player): player is PlayerStatus => Boolean(player));
}

function liveStrokesFor(player: PlayerStatus, snapshot: RoomSnapshot): number {
  if (isArcadeGameId(currentGameId(snapshot))) return Math.max(0, player.strokesThisHole);
  if (player.id === state.clientId && snapshot.phase === "playing") return Math.max(0, state.localStrokes);
  return Math.max(0, player.strokesThisHole);
}

function renderScorecard(snapshot: RoomSnapshot | undefined): void {
  const scorecard = document.querySelector("#scorecard");
  if (!scorecard) return;
  const holeCount = snapshot?.config.holeCount ?? clampHoleCount(Number(holeCountInput.value));
  const holes = currentRoundHoles(holeCount, snapshot);
  const players = snapshot?.players ?? [];
  const playMode = currentPlayMode(snapshot);
  const arcade = snapshot ? isArcadeGameId(currentGameId(snapshot)) : isArcadeGameId(state.gameId);

  const isSingle = state.mode === "single";
  const orderedPlayers = snapshot ? orderedRoomPlayers(snapshot) : players;
  const gridTemplate = isSingle ? "minmax(44px, 1fr) minmax(44px, 1fr) minmax(52px, 1fr)" : `minmax(44px, 1fr) minmax(44px, 1fr) repeat(${Math.max(1, orderedPlayers.length)}, minmax(44px, 1fr))`;
  const header = `
    <div class="score-grid score-header ${isSingle ? "single" : ""}" style="grid-template-columns: ${gridTemplate}">
      <span>${arcade ? "Round" : "Hole"}</span>
      <span>${arcade ? "Goal" : "Par"}</span>
      ${isSingle ? "<span>You</span>" : orderedPlayers.map((player) => `<span>${player.slot}</span>`).join("")}
    </div>
  `;
  const rows = holes
    .map((hole, index) => {
      const active = snapshot?.currentHole === index && snapshot.phase !== "finished";
      return `
        <div class="score-grid ${isSingle ? "single" : ""} ${active ? "active" : ""}" style="grid-template-columns: ${gridTemplate}">
          <span>${index + 1}</span>
          <span>${arcade ? "Pts" : hole.par}</span>
          ${orderedPlayers.map((player) => `<span>${scoreCell(player, index, snapshot, playMode)}</span>`).join("")}
        </div>
      `;
    })
    .join("");
  const par = holes.reduce((sum, hole) => sum + hole.par, 0);
  scorecard.innerHTML = `${header}${rows}<div class="score-total">${arcade ? `${gameById(currentGameId(snapshot)).shortTitle} ${holeCount} rounds` : playMode === "race" ? `Race ${holeCount} holes` : `Par ${par}`}</div>`;
}

function showOverlay(title: string, copy: string): void {
  overlayTitle.textContent = title;
  overlayCopy.textContent = copy;
  overlay.classList.remove("hidden");
}

function hideOverlay(): void {
  overlay.classList.add("hidden");
}

function setConnection(text: string): void {
  setText("#connection", text);
}

function setText(selector: string, value: string): void {
  const element = document.querySelector(selector);
  if (element) element.textContent = value;
}

function setActionButton(button: HTMLButtonElement, title: string, copy?: string): void {
  const titleElement = button.querySelector<HTMLElement>(".action-title");
  const copyElement = button.querySelector<HTMLElement>(".action-copy");
  if (!titleElement) {
    button.textContent = title;
    button.setAttribute("aria-label", title);
    return;
  }
  titleElement.textContent = title;
  if (copyElement && copy !== undefined) copyElement.textContent = copy;
  button.setAttribute("aria-label", title);
}

function setHoleInput(value: number): void {
  holeCountInput.value = String(clampHoleCount(value));
}

function setSelectedGame(gameId: GameId): void {
  if (state.snapshot?.phase === "playing" || state.mode === "matchmaking") return;
  state.gameId = normalizeGameId(gameId);
  if (isArcadeGameId(state.gameId)) {
    state.playMode = "race";
    state.coursePack = "classic";
  }
  if (!isPlayableGame(state.gameId)) {
    state.homePanel = "menu";
  }
  updateUi();
}

function setPlayMode(playMode: PlayMode): void {
  if (state.snapshot?.phase === "playing" || state.mode === "matchmaking") return;
  state.playMode = playMode;
  updateUi();
}

function setCoursePack(coursePack: CoursePack): void {
  if (state.snapshot?.phase === "playing" || state.mode === "matchmaking") return;
  state.coursePack = coursePack;
  updateUi();
}

function openPowerUpsPage(): void {
  if (state.snapshot?.phase === "playing" || state.mode === "matchmaking") return;
  state.powerUpsReturn = state.homePanel === "solo" ? "solo" : "host";
  state.homePanel = "powerups";
  updateUi();
}

function setPowerUp(kind: PowerUpKind, enabled: boolean): void {
  if (state.snapshot?.phase === "playing" || state.mode === "matchmaking") return;
  state.powerUps = { ...state.powerUps, [kind]: enabled };
  localStorage.setItem(POWER_UPS_KEY, JSON.stringify(state.powerUps));
  updateUi();
}

function coursePackLabel(coursePack: CoursePack): string {
  if (coursePack === "long") return "Long";
  return coursePack === "special" ? "Special" : "Classic";
}

function powerUpsSummaryText(settings: PowerUpSettings): string {
  const enabled = enabledPowerUpLabels(settings);
  if (enabled.length === 3) return "All on";
  if (enabled.length === 0) return "All off";
  if (enabled.length === 1) return `${enabled[0]} only`;
  return enabled.join(" + ");
}

function powerUpsSentence(settings: PowerUpSettings): string {
  const summary = powerUpsSummaryText(settings).toLowerCase();
  if (summary === "all on") return "all power-ups on";
  if (summary === "all off") return "power-ups off";
  return `${summary} power-ups`;
}

function powerUpsStateLabel(settings: PowerUpSettings): "on" | "off" | "mixed" {
  const enabled = enabledPowerUpLabels(settings).length;
  if (enabled === 0) return "off";
  if (enabled === 3) return "on";
  return "mixed";
}

function enabledPowerUpLabels(settings: PowerUpSettings): string[] {
  const labels: Record<PowerUpKind, string> = {
    jump: "Jump",
    bumper: "Bumper",
    portal: "Portal",
  };
  return (Object.keys(labels) as PowerUpKind[]).filter((kind) => isPowerUpEnabled(settings, kind)).map((kind) => labels[kind]);
}

function modeLabel(snapshot: RoomSnapshot | undefined): string {
  if (snapshot?.phase === "playing" && isArcadeGameId(currentGameId(snapshot))) return gameById(currentGameId(snapshot)).shortTitle;
  if (snapshot?.phase === "playing" && currentPlayMode(snapshot) === "race") return "Race";
  if (state.mode === "single") return "Solo";
  if (state.mode === "matchmaking") return "Queue";
  if (snapshot?.phase === "finished") return "Done";
  if (snapshot) return snapshot.phase === "lobby" ? "Match" : "Live";
  return "----";
}

function makeLocalPlayer(holeCount: number): PlayerStatus {
  return {
    id: state.clientId,
    slot: "A",
    name: state.playerName,
    connected: true,
    ready: true,
    color: "#ffffff",
    scores: Array(holeCount).fill(0),
    currentHoleComplete: false,
    strokesThisHole: 0,
  };
}

function getApiOrigin(): string {
  const explicit = import.meta.env.VITE_API_ORIGIN as string | undefined;
  if (explicit) return explicit.replace(/\/$/, "");
  if (location.port === "5173" || location.port === "4173") {
    return `${location.protocol}//${location.hostname}:8787`;
  }
  if (location.hostname.endsWith("pigeon-putt.pages.dev")) {
    return "https://pigeon-putt-realtime.scshome.workers.dev";
  }
  return location.origin;
}

function getClientId(): string {
  const explicit = new URLSearchParams(location.search).get("cid")?.trim();
  if (explicit) return explicit.slice(0, 80);
  const key = "pigeon-putt:client-id:session";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = makeId();
    sessionStorage.setItem(key, id);
  }
  return id;
}

function getInitialName(): string {
  return new URLSearchParams(location.search).get("name")?.trim().slice(0, 24) || localStorage.getItem("pigeon-putt:name") || "Player";
}

function getInitialPowerUps(): PowerUpSettings {
  return normalizePowerUps(localStorage.getItem(POWER_UPS_KEY));
}

function makeId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  if (typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function playTap(volume = 0.035): void {
  const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return;
  const audio = new AudioContextCtor();
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  oscillator.type = "triangle";
  oscillator.frequency.value = 180;
  gain.gain.value = volume;
  oscillator.connect(gain);
  gain.connect(audio.destination);
  oscillator.start();
  gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + 0.05);
  oscillator.stop(audio.currentTime + 0.06);
}

function drawPath(points: Point[]): void {
  ctx.beginPath();
  points.forEach((point, index) => {
    if (index === 0) ctx.moveTo(point.x, point.y);
    else ctx.lineTo(point.x, point.y);
  });
  ctx.closePath();
}

function roundRect(x: number, y: number, w: number, h: number, radius: number): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function copyPoint(point: Point): Point {
  return { x: point.x, y: point.y };
}

function blendPoint(from: Point, to: Point, amount: number): Point {
  return {
    x: from.x + (to.x - from.x) * amount,
    y: from.y + (to.y - from.y) * amount,
  };
}

function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const a = polygon[i];
    const b = polygon[j];
    const intersects = (a.y > point.y) !== (b.y > point.y) && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function polygonSignedArea(points: Point[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

function nearestPointOnPolygon(point: Point, polygon: Point[]): Point {
  let nearest = polygon[0];
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const candidate = nearestPointOnSegment(point, a, b);
    const d = distance(point, candidate);
    if (d < nearestDistance) {
      nearest = candidate;
      nearestDistance = d;
    }
  }
  return nearest;
}

function nearestPointOnSegment(point: Point, a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared < 0.001) return a;
  const t = clamp(((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared, 0, 1);
  return { x: a.x + dx * t, y: a.y + dy * t };
}

function interiorPointForHole(hole: HoleDefinition, from: Point): Point {
  const candidates = [polygonAverage(hole.fairway), hole.start, hole.cup];
  const inside = candidates.find((candidate) => pointInPolygon(candidate, hole.fairway));
  return inside ?? from;
}

function polygonAverage(points: Point[]): Point {
  const total = points.reduce(
    (sum, point) => ({
      x: sum.x + point.x,
      y: sum.y + point.y,
    }),
    { x: 0, y: 0 },
  );
  return { x: total.x / points.length, y: total.y / points.length };
}

function centroid(points: [Point, Point, Point]): Point {
  return {
    x: (points[0].x + points[1].x + points[2].x) / 3,
    y: (points[0].y + points[1].y + points[2].y) / 3,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function sumScores(scores: number[]): number {
  return scores.reduce((sum, score) => sum + (score || 0), 0);
}

function currentPlayMode(snapshot = state.snapshot): PlayMode {
  return normalizePlayMode(snapshot?.config.playMode ?? state.playMode);
}

function currentGameId(snapshot = state.snapshot): GameId {
  return normalizeGameId(snapshot?.config.gameId ?? state.gameId);
}

function currentCoursePack(snapshot = state.snapshot): CoursePack {
  return normalizeCoursePack(snapshot?.config.coursePack ?? state.coursePack);
}

function currentPowerUps(snapshot = state.snapshot): PowerUpSettings {
  return normalizePowerUps(snapshot?.config.powerUps ?? state.powerUps);
}

function currentRoundSeed(snapshot = state.snapshot): string {
  return snapshot?.config.seed ?? `solo-${state.clientId}`;
}

function currentRoundHole(index: number, snapshot = state.snapshot): HoleDefinition {
  const pack = currentCoursePack(snapshot);
  const ids = snapshot?.config.holeIds;
  const id = ids?.length ? ids[((index % ids.length) + ids.length) % ids.length] : undefined;
  return (id ? holeById(id, pack) : undefined) ?? holeForRound(index, currentRoundSeed(snapshot), pack);
}

function currentRoundHoles(holeCount: number, snapshot = state.snapshot): HoleDefinition[] {
  const pack = currentCoursePack(snapshot);
  const ids = snapshot?.config.holeIds?.slice(0, holeCount) ?? [];
  const holes = holesForRoundIds(ids, pack);
  if (holes.length === holeCount) return holes;
  return holesForRound(holeCount, currentRoundSeed(snapshot), pack);
}

function chooseClientRound(scope: string, holeCount: number, coursePack: CoursePack): RoundSelection {
  const catalog = catalogForPack(coursePack);
  const recentIds = readRecentRoundHoleIds(coursePack);
  const recentOpeners = readRecentRoundOpenerIds(coursePack);
  const lastRoundIds = recentIds.slice(0, Math.min(holeCount, recentIds.length));
  const lastRoundFamilies = lastRoundIds
    .map((id) => catalog.find((hole) => hole.id === id))
    .filter((hole): hole is HoleDefinition => Boolean(hole))
    .map(holeFamilyId);
  const recentFamilies = new Set(
    recentIds
      .map((id) => catalog.find((hole) => hole.id === id))
      .filter((hole): hole is HoleDefinition => Boolean(hole))
      .map(holeFamilyId),
  );
  const recentOpenerFamilies = new Set(
    recentOpeners
      .map((id) => catalog.find((hole) => hole.id === id))
      .filter((hole): hole is HoleDefinition => Boolean(hole))
      .map(holeFamilyId),
  );
  let bestSeed = makeClientSeed(scope);
  let bestHoleIds = holeIdsForRound(holeCount, bestSeed, coursePack, lastRoundIds, lastRoundFamilies);
  let bestScore = Number.POSITIVE_INFINITY;

  for (let attempt = 0; attempt < 256; attempt += 1) {
    const seed = makeClientSeed(`${scope}-${attempt}`);
    const ids = holeIdsForRound(holeCount, seed, coursePack, lastRoundIds, lastRoundFamilies);
    const holes = holesForRoundIds(ids, coursePack);
    const lastRoundOverlap = ids.filter((id) => lastRoundIds.includes(id)).length;
    const lastFamilyOverlap = holes.filter((hole) => lastRoundFamilies.includes(holeFamilyId(hole))).length;
    const idOverlap = ids.filter((id) => recentIds.includes(id)).length;
    const familyOverlap = holes.filter((hole) => recentFamilies.has(holeFamilyId(hole))).length;
    const openerIdRepeat = recentOpeners.includes(ids[0]) ? 30 : 0;
    const openerFamilyRepeat = recentOpenerFamilies.has(holeFamilyId(holes[0])) ? 14 : 0;
    const score = lastRoundOverlap * 500 + lastFamilyOverlap * 90 + idOverlap * 5 + familyOverlap + openerIdRepeat + openerFamilyRepeat;

    if (score < bestScore) {
      bestScore = score;
      bestSeed = seed;
      bestHoleIds = ids;
    }
    if (score === 0) break;
  }

  return { seed: bestSeed, holeIds: bestHoleIds };
}

function makeClientSeed(scope: string): string {
  const random = new Uint32Array(2);
  crypto.getRandomValues(random);
  return `${scope}-${Date.now().toString(36)}-${random[0].toString(36)}-${random[1].toString(36)}`;
}

function rememberClientRound(ids: number[], coursePack: CoursePack): void {
  const previous = readRecentRoundHoleIds(coursePack);
  const next = [...ids, ...previous.filter((id) => !ids.includes(id))].slice(0, RECENT_ROUND_HOLE_LIMIT);
  const previousOpeners = readRecentRoundOpenerIds(coursePack);
  const nextOpeners = [ids[0], ...previousOpeners.filter((id) => id !== ids[0])].slice(0, RECENT_ROUND_OPENER_LIMIT);
  try {
    localStorage.setItem(recentStorageKey(RECENT_ROUND_HOLES_KEY, coursePack), JSON.stringify(next));
    localStorage.setItem(recentStorageKey(RECENT_ROUND_OPENERS_KEY, coursePack), JSON.stringify(nextOpeners));
  } catch {
    // Non-critical: private browsing/storage failures should not block play.
  }
}

function readRecentRoundHoleIds(coursePack: CoursePack): number[] {
  return readStoredIds(recentStorageKey(RECENT_ROUND_HOLES_KEY, coursePack), RECENT_ROUND_HOLE_LIMIT);
}

function readRecentRoundOpenerIds(coursePack: CoursePack): number[] {
  return readStoredIds(recentStorageKey(RECENT_ROUND_OPENERS_KEY, coursePack), RECENT_ROUND_OPENER_LIMIT);
}

function recentStorageKey(baseKey: string, coursePack: CoursePack): string {
  return coursePack === "classic" ? baseKey : `${baseKey}:${coursePack}`;
}

function readStoredIds(key: string, limit: number): number[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is number => Number.isInteger(value) && value > 0).slice(0, limit);
  } catch {
    return [];
  }
}

function sortStandings(players: PlayerStatus[], playMode: PlayMode): PlayerStatus[] {
  return [...players].sort((a, b) => {
    const aTotal = sumScores(a.scores);
    const bTotal = sumScores(b.scores);
    return playMode === "race" ? bTotal - aTotal : aTotal - bTotal;
  });
}

function holeWinner(players: PlayerStatus[], holeIndex: number, playMode: PlayMode): PlayerStatus | undefined {
  const scored = players.filter((player) => (player.scores[holeIndex] || 0) > 0);
  if (scored.length === 0) return undefined;
  const sorted = [...scored].sort((a, b) => {
    const aScore = a.scores[holeIndex] || 0;
    const bScore = b.scores[holeIndex] || 0;
    return playMode === "race" ? bScore - aScore : aScore - bScore;
  });
  const first = sorted[0];
  const second = sorted[1];
  if (!second) return first;
  if ((first.scores[holeIndex] || 0) === (second.scores[holeIndex] || 0)) return undefined;
  return first;
}

function scoreSummary(players: PlayerStatus[], playMode: PlayMode): string {
  return sortStandings(players, playMode)
    .map((player) => `${player.name} ${sumScores(player.scores)}`)
    .join(" · ");
}

function scoreCell(player: PlayerStatus | undefined, index: number, snapshot: RoomSnapshot | undefined, playMode: PlayMode): string {
  if (!player) return "-";
  const score = player.scores[index] || 0;
  if (score > 0) return String(score);
  if (playMode === "race" && snapshot && (snapshot.phase === "finished" || index < snapshot.currentHole)) return "0";
  return "-";
}

function playerRoleLabel(slot: PlayerSlot): string {
  if (state.mode === "single") return "Solo";
  return slot === "A" ? "Host" : `Player ${slot}`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return entities[char] ?? char;
  });
}

function must<T>(value: T | null | undefined, label: string): T {
  if (!value) throw new Error(`Missing ${label}`);
  return value;
}

function publishDebugState(): void {
  const rect = canvas.getBoundingClientRect();
  const gameId = currentGameId();
  const coursePack = currentCoursePack();
  const catalog = catalogForPack(coursePack);
  const powerUps = currentPowerUps();
  const activePowerUpZones = state.currentHole.zones.filter((zone) => isPowerUpZone(zone) && isPowerUpEnabled(powerUps, zone.kind));
  const roundHoles = currentRoundHoles(state.snapshot?.config.holeCount ?? clampHoleCount(Number(holeCountInput.value)));
  (window as unknown as { __pigeonDebug?: unknown }).__pigeonDebug = {
    ball: state.ball
      ? {
          x: state.ball.position.x,
          y: state.ball.position.y,
          vx: state.ball.velocity.x,
          vy: state.ball.velocity.y,
          speed: Vector.magnitude(state.ball.velocity),
        }
      : null,
    camera: state.camera,
    canShoot: canShoot(),
    catalogIds: catalog.map((hole) => hole.id),
    canvas: {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      backingWidth: canvas.width,
      backingHeight: canvas.height,
      dpr: window.devicePixelRatio || 1,
    },
    config: state.snapshot?.config ?? null,
    gameId,
    games: GAME_CATALOG,
    arcade: arcadeRuntime.debug(),
    coursePack,
    controlMode: state.controlMode,
    powerUps,
    edge: { ballInFairway: state.ball ? pointInPolygon(state.ball.position, state.currentHole.fairway) : false },
    hole: {
      id: state.currentHole.id,
      familyId: holeFamilyId(state.currentHole),
      index: state.localHole,
      start: state.currentHole.start,
      cup: state.currentHole.cup,
      name: state.currentHole.name,
      size: state.currentHole.size,
      distance: distance(state.currentHole.start, state.currentHole.cup),
      fairway: state.currentHole.fairway,
      obstacles: state.currentHole.obstacles,
      zones: state.currentHole.zones,
      movingRails: state.currentHole.obstacles.filter((obstacle) => obstacle.kind === "movingRail").length,
      rotatingBunkers: state.currentHole.zones.filter((zone) => zone.kind === "rotatingBunker").length,
      jumps: activePowerUpZones.filter((zone) => zone.kind === "jump").length,
      bumpers: activePowerUpZones.filter((zone) => zone.kind === "bumper").length,
      portals: activePowerUpZones.filter((zone) => zone.kind === "portal").length,
      funFeatures: activePowerUpZones.length,
      features: activePowerUpZones.map((zone, index) => ({
          kind: zone.kind,
          x: zone.x,
          y: zone.y,
          r: zone.r,
          angle: zone.angle,
          group: zone.group,
          exit: zone.exit,
          index,
        })),
    },
    roundHoles: roundHoles.map((hole, index) => ({
      index,
      id: hole.id,
      familyId: holeFamilyId(hole),
      name: hole.name,
      par: hole.par,
    })),
    ghosts: Array.from(state.ghosts.entries()).map(([id, ghost]) => ({
      id,
      slot: ghost.slot,
      moving: ghost.moving,
      body: {
        x: ghost.body.position.x,
        y: ghost.body.position.y,
        vx: ghost.body.velocity.x,
        vy: ghost.body.velocity.y,
        speed: Vector.magnitude(ghost.body.velocity),
      },
      render: copyPoint(ghost.renderPosition),
      target: copyPoint(ghost.targetPosition),
      targetVelocity: copyPoint(ghost.targetVelocity),
      visualGap: distance(ghost.renderPosition, ghost.body.position),
    })),
    holes: catalog.length,
    aim:
      state.pointer.type === "aim"
        ? (() => {
            const aim = aimVector(state.pointer);
            const speed = shotSpeedFromPower(aim.power);
            return { power: aim.power, speed, lineLength: aimLineLength(speed) };
          })()
        : null,
    phase: state.snapshot?.phase ?? "offline",
    players: state.snapshot?.players.map((player) => ({
      id: player.id,
      slot: player.slot,
      name: player.name,
      connected: player.connected,
      ready: player.ready,
      scores: player.scores,
      currentHoleComplete: player.currentHoleComplete,
      strokesThisHole: player.strokesThisHole,
    })) ?? [],
    room: state.snapshot?.code ?? null,
    socketState: state.socket?.readyState ?? null,
    strokes: state.localStrokes,
    finishHole: debugFinishHole,
    placeBall: debugPlaceBall,
    setCatalogHole: debugSetCatalogHole,
    setCoursePack: debugSetCoursePack,
    setHole: debugSetHole,
  };
}

function debugSetHole(index: number): void {
  const catalog = catalogForPack(state.coursePack);
  const nextHole = clamp(Math.floor(index), 0, catalog.length - 1);
  if (state.mode !== "single" || !state.snapshot) {
    startSinglePlayer();
  }
  if (!state.snapshot) return;
  const holeCount = clampHoleCount(Math.max(state.snapshot.config.holeCount, nextHole + 1));
  state.snapshot.config.holeCount = holeCount;
  state.snapshot.players[0].scores = Array(holeCount).fill(0);
  state.snapshot.currentHole = nextHole;
  state.snapshot.players[0].currentHoleComplete = false;
  state.snapshot.players[0].strokesThisHole = 0;
  state.snapshot.phase = "playing";
  state.mode = "single";
  state.localSlot = "A";
  state.controlMode = "shoot";
  loadHole(nextHole);
  updateUi();
}

function debugSetCatalogHole(index: number, coursePack: CoursePack = state.coursePack): void {
  state.coursePack = normalizeCoursePack(coursePack);
  const catalog = catalogForPack(state.coursePack);
  const nextHole = clamp(Math.floor(index), 0, catalog.length - 1);
  if (state.mode !== "single" || !state.snapshot) {
    startSinglePlayer();
  }
  if (!state.snapshot) return;
  const holeCount = clampHoleCount(Math.max(state.snapshot.config.holeCount, nextHole + 1));
  state.snapshot.config.holeCount = holeCount;
  state.snapshot.players[0].scores = Array(holeCount).fill(0);
  state.snapshot.currentHole = nextHole;
  state.snapshot.players[0].currentHoleComplete = false;
  state.snapshot.players[0].strokesThisHole = 0;
  state.snapshot.phase = "playing";
  state.mode = "single";
  state.localSlot = "A";
  state.controlMode = "shoot";
  loadHole(nextHole, holeFor(nextHole, state.coursePack));
  updateUi();
}

function debugSetCoursePack(coursePack: CoursePack): void {
  if (state.snapshot?.phase === "playing" && state.mode !== "single") return;
  state.coursePack = normalizeCoursePack(coursePack);
  if (state.mode === "single" && state.snapshot) {
    startSinglePlayer();
  } else {
    loadHole(0, holeFor(0, state.coursePack));
    updateUi();
  }
}

function debugFinishHole(): void {
  if (!state.ball || state.snapshot?.phase !== "playing") return;
  state.localStrokes = Math.max(1, state.localStrokes);
  Body.setPosition(state.ball, state.currentHole.cup);
  Body.setVelocity(state.ball, { x: 0, y: 0 });
  state.holeComplete = true;
  state.didReportComplete = true;
  if (state.mode === "single") {
    completeSingleHole(state.localStrokes);
  } else {
    send({ type: "hole_complete", hole: state.localHole, strokes: state.localStrokes });
  }
  updateUi();
}

function debugPlaceBall(point: Point, velocity: Point = { x: 0, y: 0 }): void {
  if (!state.ball) return;
  Body.setPosition(state.ball, point);
  Body.setVelocity(state.ball, velocity);
  Body.setAngularVelocity(state.ball, 0);
}
